import React, { useEffect, useMemo, useState } from "react";
import { Redirect, Route, Switch, useHistory, useLocation } from "react-router-dom";
import {
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  List,
  ListItem,
  MenuItem,
  Paper,
  Select,
  Tab,
  Tabs,
  TextField,
  Typography,
  makeStyles,
} from "@material-ui/core";
import api from "util/Api";
import ContainerHeader from "components/ContainerHeader/index";
import { USER_DATA } from "../../../constants/ActionTypes";
import { getUserServer } from "../../../util/localStorageService";
import {
  createHistoryCompareOptions,
  getCompareTargetLabel,
  GRAPH_STATE_OPTIONS,
  GRAPH_STATE_OPTIONS_WITH_ALL,
  NotificationChangesPanel,
  NotificationDependencyGraphPanel,
  NotificationLocalePanel,
  NotificationSchedulePanel,
} from "./workbenchPanels";

const BRANDS = ["Flip", "IraClub"];
const DEFAULT_LOCALE = "en-US";
const LOCK_FILTERS = [
  { value: "all", label: "All" },
  { value: "locked", label: "Locked" },
  { value: "unlocked", label: "Unlocked" },
];
const PREVIEW_STATES = [
  { value: "Draft", label: "Draft" },
  { value: "Published", label: "Published" },
];
const MIGRATION_STATUSES = [
  { value: "all", label: "All" },
  { value: "managed", label: "Managed" },
  { value: "partial", label: "Partial" },
  { value: "legacy-dependent", label: "Legacy" },
];
const IMAGE_LIBRARY_FILTERS = [
  { value: "", label: "All" },
  { value: "unused", label: "Unused" },
  { value: "draft only", label: "Draft Only" },
  { value: "published", label: "Published" },
  { value: "archived", label: "Archived" },
  { value: "duplicate hash", label: "Duplicate Hash" },
];
const useStyles = makeStyles((theme) => ({
  page: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(3),
    paddingBottom: theme.spacing(4),
  },
  shell: {
    padding: theme.spacing(4),
    borderRadius: 22,
    background:
      "linear-gradient(135deg, rgba(255,255,255,0.98), rgba(247,244,236,0.98))",
    boxShadow: "0 20px 45px rgba(23, 31, 50, 0.08)",
  },
  toolbar: {
    display: "flex",
    flexWrap: "wrap",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: theme.spacing(2),
    marginBottom: theme.spacing(3),
  },
  statusStack: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1.5),
    marginBottom: theme.spacing(3),
  },
  toolbarActions: {
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(2),
    alignItems: "center",
  },
  toolbarBlock: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(2.5),
    padding: theme.spacing(3),
    marginBottom: theme.spacing(3),
    borderRadius: 20,
    border: "1px solid rgba(23, 31, 50, 0.08)",
    background:
      "linear-gradient(180deg, rgba(255,255,255,0.98), rgba(251,250,247,0.98))",
    boxShadow: "0 10px 24px rgba(23, 31, 50, 0.04)",
  },
  toolbarSupport: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1.5),
  },
  toolbarNote: {
    color: "#5f6676",
    lineHeight: 1.5,
    maxWidth: 780,
  },
  title: {
    fontWeight: 700,
    color: "#171f32",
  },
  subtitle: {
    color: "#5f6676",
    maxWidth: 760,
    marginTop: theme.spacing(1),
  },
  navTabs: {
    marginBottom: theme.spacing(3),
    backgroundColor: "#ffffff",
    borderRadius: 16,
    border: "1px solid rgba(23, 31, 50, 0.08)",
  },
  listPanel: {
    padding: theme.spacing(3),
    borderRadius: 20,
    border: "1px solid rgba(23, 31, 50, 0.08)",
    backgroundColor: "#ffffff",
    boxShadow: "0 10px 24px rgba(23, 31, 50, 0.04)",
  },
  editorPanel: {
    padding: theme.spacing(3.5),
    borderRadius: 20,
    border: "1px solid rgba(23, 31, 50, 0.08)",
    backgroundColor: "#ffffff",
    minHeight: 420,
    boxShadow: "0 10px 24px rgba(23, 31, 50, 0.04)",
  },
  searchField: {
    marginBottom: theme.spacing(3),
  },
  filterRow: {
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(1.25),
    marginBottom: theme.spacing(3),
  },
  groupCard: {
    borderRadius: 14,
    overflow: "hidden",
    border: "1px solid rgba(23, 31, 50, 0.08)",
    marginBottom: theme.spacing(2),
  },
  groupHeader: {
    padding: theme.spacing(2, 2.5),
    background: "linear-gradient(90deg, #171f32, #34415e)",
    color: "#ffffff",
    fontWeight: 600,
  },
  list: {
    padding: 0,
  },
  row: {
    display: "block",
    width: "100%",
    textAlign: "left",
    padding: theme.spacing(2.5, 3),
  },
  rowInner: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: theme.spacing(3),
  },
  rowMeta: {
    minWidth: 140,
    textAlign: "right",
    color: "#6f7687",
    lineHeight: 1.45,
  },
  rowTitle: {
    fontWeight: 600,
    color: "#171f32",
  },
  rowKey: {
    fontFamily: '"Roboto Mono", "Courier New", monospace',
    fontSize: 12,
    color: "#6f7687",
    marginTop: theme.spacing(0.5),
  },
  rowDescription: {
    color: "#5f6676",
    marginTop: theme.spacing(0.75),
  },
  chipRow: {
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(1.25),
    marginTop: theme.spacing(1.25),
  },
  compactChipRow: {
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(1.25),
    marginTop: 0,
  },
  status: {
    padding: theme.spacing(1.5, 2),
    borderRadius: 12,
    fontWeight: 500,
  },
  success: {
    backgroundColor: "#edf7ed",
    color: "#1b5e20",
  },
  error: {
    backgroundColor: "#fdecea",
    color: "#b71c1c",
  },
  loadingWrap: {
    display: "flex",
    justifyContent: "center",
    padding: theme.spacing(5),
  },
  emptyState: {
    height: "100%",
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    textAlign: "center",
    color: "#5f6676",
    border: "1px dashed rgba(23, 31, 50, 0.12)",
    borderRadius: 14,
    padding: theme.spacing(4),
  },
  sectionTitle: {
    fontWeight: 600,
    color: "#171f32",
    marginBottom: theme.spacing(1.5),
  },
  field: {
    marginBottom: theme.spacing(2),
  },
  selectField: {
    minWidth: 220,
    width: "100%",
    maxWidth: 280,
  },
  selectRow: {
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(2),
    alignItems: "flex-end",
    marginBottom: theme.spacing(3),
  },
  editorMeta: {
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(1.25),
    marginBottom: 0,
  },
  collapsibleSectionHeader: {
    display: "flex",
    flexWrap: "wrap",
    alignItems: "center",
    justifyContent: "space-between",
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  collapsibleSectionMeta: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(0.5),
  },
  collapsibleSectionTitle: {
    fontWeight: 600,
    color: "#171f32",
  },
  collapsibleSectionDescription: {
    color: "#5f6676",
  },
  actionRow: {
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(1.5),
    marginTop: theme.spacing(2),
  },
  editorHeader: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(2),
    marginBottom: theme.spacing(3),
  },
  brandLocaleRow: {
    display: "flex",
    flexWrap: "wrap",
    alignItems: "flex-end",
    justifyContent: "space-between",
    gap: theme.spacing(3),
    marginBottom: theme.spacing(4),
  },
  brandTabs: {
    flex: "1 1 320px",
    minHeight: 48,
  },
  localeControl: {
    width: 248,
    marginBottom: theme.spacing(0.5),
  },
  sectionBlock: {
    marginTop: theme.spacing(5),
    paddingTop: theme.spacing(4),
    borderTop: "1px solid rgba(23, 31, 50, 0.08)",
  },
  destinationCard: {
    padding: theme.spacing(2.5),
    borderRadius: 16,
    border: "1px solid rgba(23, 31, 50, 0.08)",
    backgroundColor: "#fbfaf7",
    height: "100%",
  },
  destinationHelp: {
    marginTop: theme.spacing(1.25),
    color: "#5f6676",
    lineHeight: 1.55,
  },
  dialogList: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1),
    marginTop: theme.spacing(2),
  },
  tokenList: {
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(1),
    marginTop: theme.spacing(1),
  },
  versionCard: {
    padding: theme.spacing(3.5),
    borderRadius: 18,
    border: "1px solid rgba(23, 31, 50, 0.08)",
    backgroundColor: "#fbfaf7",
    height: "100%",
  },
  codeBlock: {
    marginTop: theme.spacing(1.5),
    padding: theme.spacing(1.5),
    borderRadius: 12,
    backgroundColor: "#171f32",
    color: "#f8f6ef",
    fontFamily: '"Roboto Mono", "Courier New", monospace',
    fontSize: 12,
    overflowX: "auto",
    whiteSpace: "pre-wrap",
    wordBreak: "break-word",
  },
  previewFrame: {
    width: "100%",
    minHeight: 360,
    border: "1px solid rgba(23, 31, 50, 0.12)",
    borderRadius: 12,
    backgroundColor: "#ffffff",
  },
  historyItem: {
    borderRadius: 12,
    border: "1px solid rgba(23, 31, 50, 0.08)",
    marginBottom: theme.spacing(1.5),
    padding: theme.spacing(2),
  },
  imageCard: {
    padding: theme.spacing(2),
    borderRadius: 14,
    border: "1px solid rgba(23, 31, 50, 0.08)",
    backgroundColor: "#ffffff",
    height: "100%",
  },
  imagePreview: {
    width: "100%",
    maxHeight: 160,
    objectFit: "contain",
    borderRadius: 12,
    backgroundColor: "#fbfaf7",
    border: "1px solid rgba(23, 31, 50, 0.08)",
    marginBottom: theme.spacing(1.5),
  },
  plainInput: {
    display: "block",
    marginBottom: theme.spacing(2),
  },
  issueItem: {
    padding: theme.spacing(1.25),
    borderRadius: 12,
    marginBottom: theme.spacing(1),
    backgroundColor: "#f7f4ec",
    border: "1px solid rgba(23, 31, 50, 0.08)",
  },
  usageList: {
    marginTop: theme.spacing(1.5),
  },
  "@media (max-width: 960px)": {
    shell: {
      padding: theme.spacing(2.5),
    },
    toolbarBlock: {
      padding: theme.spacing(2.5),
    },
    editorPanel: {
      padding: theme.spacing(2.5),
    },
    row: {
      padding: theme.spacing(2.25),
    },
    rowInner: {
      flexDirection: "column",
      gap: theme.spacing(1.5),
    },
    rowMeta: {
      minWidth: "auto",
      textAlign: "left",
    },
    brandLocaleRow: {
      alignItems: "stretch",
    },
    localeControl: {
      width: "100%",
      marginBottom: 0,
    },
  },
}));

const formatDate = (value) => {
  if (!value) {
    return "Not available";
  }

  return new Date(value).toLocaleString();
};

const getFirstLocale = (locales) => {
  if (!locales || locales.length === 0) {
    return DEFAULT_LOCALE;
  }

  return locales.indexOf(DEFAULT_LOCALE) >= 0 ? DEFAULT_LOCALE : locales[0];
};

const getPreferredBrand = (brands, fallback = BRANDS[0]) => {
  if (!brands || brands.length === 0) {
    return fallback;
  }

  return brands.indexOf(fallback) >= 0 ? fallback : brands[0];
};

const findPushTemplateInSections = (groups, templateKey) => {
  if (!templateKey) {
    return null;
  }

  for (const group of groups || []) {
    const match =
      (group.templates || []).find(
        (template) => template.templateKey === templateKey
      ) || null;

    if (match) {
      return match;
    }
  }

  return null;
};

const stringifyParameterJson = (value) => JSON.stringify(value || {}, null, 2);

const buildSampleParameterJson = (tokens) => {
  const output = {};

  (tokens || []).forEach((token) => {
    output[token] = `<${token}>`;
  });

  return stringifyParameterJson(output);
};

const parseParameterJson = (value) => {
  if (!value || !value.trim()) {
    return {};
  }

  const parsed = JSON.parse(value);
  return parsed && typeof parsed === "object" ? parsed : {};
};

const tryParseParameterJson = (value) => {
  try {
    return parseParameterJson(value);
  } catch (error) {
    return null;
  }
};

const collectAllowedTokens = (...tokenSets) =>
  tokenSets
    .reduce((allTokens, tokenSet) => allTokens.concat(tokenSet || []), [])
    .filter(Boolean)
    .filter((token, index, values) => values.indexOf(token) === index);

const buildTokenInputValues = (tokens, previewJson) => {
  const parsed = tryParseParameterJson(previewJson) || {};
  const values = {};

  (tokens || []).forEach((token) => {
    const rawValue = parsed[token];
    values[token] = rawValue == null ? "" : `${rawValue}`;
  });

  return values;
};

const updateTokenValueInJson = (previewJson, token, value) => {
  const parsed = tryParseParameterJson(previewJson) || {};
  parsed[token] = value;
  return stringifyParameterJson(parsed);
};

const toDisplayMessage = (value, fallback = "") => {
  if (typeof value === "string" && value.trim()) {
    return value;
  }

  if (Array.isArray(value)) {
    const messages = value
      .map((entry) => toDisplayMessage(entry, ""))
      .filter(Boolean);
    return messages.length > 0 ? messages.join(", ") : fallback;
  }

  if (value && typeof value === "object") {
    if (typeof value.message === "string" && value.message.trim()) {
      return value.message;
    }

    if (typeof value.error === "string" && value.error.trim()) {
      return value.error;
    }

    if (value.error) {
      return toDisplayMessage(value.error, fallback);
    }

    try {
      return JSON.stringify(value);
    } catch (stringifyError) {
      return fallback;
    }
  }

  return fallback;
};

const getApiErrorMessage = (requestError, fallback) =>
  toDisplayMessage(
    (requestError &&
      requestError.response &&
      requestError.response.data &&
      (requestError.response.data.error || requestError.response.data.message)) ||
      (requestError && requestError.message),
    fallback
  );

const parseDateTimeInput = (value) => {
  if (!value) {
    return null;
  }

  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed.toISOString();
};

const formatBytes = (value) => {
  const numericValue = Number(value || 0);

  if (numericValue < 1024) {
    return `${numericValue} B`;
  }

  if (numericValue < 1024 * 1024) {
    return `${Math.round(numericValue / 1024)} KB`;
  }

  return `${(numericValue / (1024 * 1024)).toFixed(1)} MB`;
};

const parseCsv = (value) =>
  (value || "")
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean);

const formatAssetReferenceNames = (assets) =>
  (assets || []).map((asset) => asset.displayName || asset.notificationImageAssetId);

const removeImageReferencesFromDependencyGraph = (graph) => {
  if (!graph) {
    return graph;
  }

  const imageNodes = (graph.nodes || []).filter(
    (node) => node && (node.notificationImageAssetId || node.nodeType === "ImageAsset")
  );
  const imageNodeIds = new Set(imageNodes.map((node) => node.nodeId).filter(Boolean));
  const imageLabels = imageNodes
    .map((node) => (node && node.label ? String(node.label).toLowerCase() : ""))
    .filter(Boolean);

  return {
    ...graph,
    nodes: (graph.nodes || []).filter((node) => !imageNodeIds.has(node.nodeId)),
    edges: (graph.edges || []).filter(
      (edge) =>
        edge &&
        edge.relationship !== "references-image" &&
        !imageNodeIds.has(edge.sourceNodeId) &&
        !imageNodeIds.has(edge.targetNodeId)
    ),
    warnings: (graph.warnings || []).filter((warning) => {
      const normalizedWarning = String(warning || "").toLowerCase();

      if (normalizedWarning.includes("image")) {
        return false;
      }

      return imageLabels.every((label) => normalizedWarning.indexOf(label) < 0);
    }),
  };
};

const copyToClipboard = async (value) => {
  if (!value) {
    return false;
  }

  if (navigator.clipboard && navigator.clipboard.writeText) {
    await navigator.clipboard.writeText(value);
    return true;
  }

  const textarea = document.createElement("textarea");
  textarea.value = value;
  textarea.setAttribute("readonly", "readonly");
  textarea.style.position = "absolute";
  textarea.style.left = "-9999px";
  document.body.appendChild(textarea);
  textarea.select();
  document.execCommand("copy");
  document.body.removeChild(textarea);
  return true;
};

const insertReference = (currentValue, referenceValue) => {
  if (!referenceValue) {
    return currentValue || "";
  }

  if (!currentValue) {
    return referenceValue;
  }

  return `${currentValue}\n${referenceValue}`;
};

const filterGroups = (groups, search, lockFilter) => {
  const query = (search || "").trim().toLowerCase();

  return (groups || [])
    .map((group) => ({
      ...group,
      templates: (group.templates || []).filter((template) => {
        const matchesLock =
          lockFilter === "all" ||
          (lockFilter === "locked" && template.hasLockedLocales) ||
          (lockFilter === "unlocked" && !template.hasLockedLocales);

        if (!matchesLock) {
          return false;
        }

        if (!query) {
          return true;
        }

        const haystack = [
          template.displayName,
          template.templateKey,
          template.description,
          template.domain,
          template.section,
          template.baseTemplateKey,
          template.provenance,
        ]
          .join(" ")
          .toLowerCase();

        return haystack.indexOf(query) >= 0;
      }),
    }))
    .filter((group) => (group.templates || []).length > 0);
};

const collectVisibleTemplateKeys = (groups) =>
  (groups || [])
    .reduce((allTemplates, group) => allTemplates.concat(group.templates || []), [])
    .map((template) => template.templateKey)
    .filter((value, index, values) => values.indexOf(value) === index);

const createEmptyPushTemplateDraft = () => ({
  bindingKey: "",
  templateKey: "",
  displayName: "",
  section: "",
  description: "",
  title: "",
  body: "",
});

const applyPushBindingToDraft = (binding) => {
  if (!binding) {
    return createEmptyPushTemplateDraft();
  }

  return {
    bindingKey: binding.bindingKey || "",
    templateKey: binding.templateKey || "",
    displayName: binding.displayName || "",
    section: binding.section || "",
    description: binding.description || "",
    title: "",
    body: "",
  };
};

const TEST_DESTINATION_STORAGE_KEY_PREFIX = "notification-template-test-destination";

const filterMigrationItems = (items, search, status) =>
  (items || []).filter((item) => {
    if (status !== "all" && item.status !== status) {
      return false;
    }

    const query = (search || "").trim().toLowerCase();
    if (!query) {
      return true;
    }

    const haystack = [
      item.templateKey,
      item.displayName,
      item.status,
      item.sourceTemplateFile,
      (item.brands || []).join(" "),
      (item.managedKinds || []).join(" "),
      (item.missingManagedKinds || []).join(" "),
    ]
      .join(" ")
      .toLowerCase();

    return haystack.indexOf(query) >= 0;
  });

const normalizeTestDestinationOverride = (value) => {
  if (!value || typeof value !== "object") {
    return null;
  }

  const userId = value.userId ? `${value.userId}`.trim() : "";
  const emailAddress = value.emailAddress ? `${value.emailAddress}`.trim() : "";
  const tradingAccountNumber = value.tradingAccountNumber
    ? `${value.tradingAccountNumber}`.trim()
    : "";

  if (!userId && !emailAddress && !tradingAccountNumber) {
    return null;
  }

  const normalized = {};

  if (userId) {
    normalized.userId = userId;
  }

  if (emailAddress) {
    normalized.emailAddress = emailAddress;
  }

  if (tradingAccountNumber) {
    normalized.tradingAccountNumber = tradingAccountNumber;
  }

  return normalized;
};

const getStoredAuthUserId = () => {
  try {
    const rawUser = localStorage.getItem(USER_DATA);

    if (!rawUser) {
      return null;
    }

    const parsedUser = JSON.parse(rawUser);
    return parsedUser && parsedUser.Id ? `${parsedUser.Id}`.trim() : null;
  } catch (error) {
    return null;
  }
};

const getStoredTestDestinationServer = () => getUserServer();

const getTestDestinationStorageKey = (authUserId, server) =>
  `${TEST_DESTINATION_STORAGE_KEY_PREFIX}:${server || "default"}:${authUserId || "anonymous"}`;

const loadStoredTestDestinationOverride = (authUserId, server) => {
  try {
    const rawValue = localStorage.getItem(
      getTestDestinationStorageKey(authUserId, server)
    );

    if (!rawValue) {
      return null;
    }

    return normalizeTestDestinationOverride(JSON.parse(rawValue));
  } catch (error) {
    return null;
  }
};

const persistStoredTestDestinationOverride = (authUserId, server, value) => {
  const storageKey = getTestDestinationStorageKey(authUserId, server);
  const normalized = normalizeTestDestinationOverride(value);

  if (!normalized) {
    localStorage.removeItem(storageKey);
    return null;
  }

  localStorage.setItem(storageKey, JSON.stringify(normalized));
  return normalized;
};

const clearStoredTestDestinationOverride = (authUserId, server) => {
  localStorage.removeItem(getTestDestinationStorageKey(authUserId, server));
};

const getTestDestinationChipLabels = (override) => {
  const normalized = normalizeTestDestinationOverride(override);

  if (!normalized) {
    return [];
  }

  return [
    normalized.userId ? { key: "userId", label: `User ID: ${normalized.userId}` } : null,
    normalized.emailAddress
      ? { key: "emailAddress", label: `Email: ${normalized.emailAddress}` }
      : null,
    normalized.tradingAccountNumber
      ? {
          key: "tradingAccountNumber",
          label: `Trading Account: ${normalized.tradingAccountNumber}`,
        }
      : null,
  ].filter(Boolean);
};

const buildTestSendRequestBody = (previewJson, previewState, destinationOverride) => {
  const normalizedOverride = normalizeTestDestinationOverride(destinationOverride);
  const body = {
    parameters: parseParameterJson(previewJson),
    state: previewState,
  };

  if (normalizedOverride) {
    body.destinationOverride = normalizedOverride;
  }

  return body;
};

const describeResolvedTestDestination = (result) => {
  if (!result) {
    return "the selected destination";
  }

  if (result.targetType === "tradingAccount" && result.resolvedTradingAccountNumber) {
    return `trading account ${result.resolvedTradingAccountNumber}`;
  }

  if (result.targetType === "email" && result.resolvedEmailAddress) {
    return result.resolvedEmailAddress;
  }

  if (result.targetType === "user" && result.resolvedUserId) {
    return `user ${result.resolvedUserId}`;
  }

  if (result.resolvedEmailAddress) {
    return result.resolvedEmailAddress;
  }

  if (result.resolvedUserId) {
    return `user ${result.resolvedUserId}`;
  }

  return "the selected destination";
};

const useStoredTestDestinationOverride = () => {
  const authUserId = useMemo(() => getStoredAuthUserId(), []);
  const server = useMemo(() => getStoredTestDestinationServer(), []);
  const [override, setOverride] = useState(() =>
    loadStoredTestDestinationOverride(authUserId, server)
  );

  const saveOverride = (value) => {
    const normalized = persistStoredTestDestinationOverride(authUserId, server, value);
    setOverride(normalized);
    return normalized;
  };

  const clearOverride = () => {
    clearStoredTestDestinationOverride(authUserId, server);
    setOverride(null);
  };

  return {
    authUserId,
    server,
    override,
    hasOverride: Boolean(override),
    saveOverride,
    clearOverride,
  };
};

const StatusBanners = ({ status, error }) => {
  const classes = useStyles();
  const statusMessage = toDisplayMessage(status);
  const errorMessage = toDisplayMessage(error);

  if (!statusMessage && !errorMessage) {
    return null;
  }

  return (
    <div className={classes.statusStack}>
      {statusMessage ? (
        <div className={`${classes.status} ${classes.success}`}>{statusMessage}</div>
      ) : null}
      {errorMessage ? (
        <div className={`${classes.status} ${classes.error}`}>{errorMessage}</div>
      ) : null}
    </div>
  );
};

const LintIssuesPanel = ({ issues, emptyLabel }) => {
  const classes = useStyles();

  if (!issues || issues.length === 0) {
    return (
      <Typography variant="body2" color="textSecondary">
        {emptyLabel || "No lint issues."}
      </Typography>
    );
  }

  return (
    <div>
      {issues.map((issue, index) => (
        <div key={`${issue.code}-${index}`} className={classes.issueItem}>
          <Typography variant="subtitle2">
            {issue.severity ? issue.severity.toUpperCase() : "INFO"}: {issue.code}
          </Typography>
          <Typography variant="body2">{issue.message}</Typography>
        </div>
      ))}
    </div>
  );
};

const TranslationDialog = ({
  open,
  locales,
  selectedLocales,
  onToggleLocale,
  onClose,
  onSubmit,
  busy,
}) => {
  const classes = useStyles();

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Add Languages</DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" color="textSecondary">
          This queues a background translation job for the currently visible
          templates. New translations land as drafts and must be published
          explicitly.
        </Typography>
        <div className={classes.dialogList}>
          {(locales || [])
            .filter((locale) => !locale.isDefault)
            .map((locale) => (
              <FormControlLabel
                key={locale.locale}
                control={
                  <Checkbox
                    color="primary"
                    checked={selectedLocales.indexOf(locale.locale) >= 0}
                    onChange={() => onToggleLocale(locale.locale)}
                  />
                }
                label={locale.displayName}
              />
            ))}
        </div>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          color="primary"
          variant="contained"
          onClick={onSubmit}
          disabled={busy || selectedLocales.length === 0}
        >
          {busy ? "Queueing..." : "Queue Translation Job"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const TestDestinationDialog = ({
  open,
  override,
  busy,
  onClose,
  onSave,
  server,
  allowEmailAddress = true,
}) => {
  const classes = useStyles();
  const [userId, setUserId] = useState("");
  const [emailAddress, setEmailAddress] = useState("");
  const [tradingAccountNumber, setTradingAccountNumber] = useState("");

  useEffect(() => {
    if (!open) {
      return;
    }

    const normalized = normalizeTestDestinationOverride(override);
    setUserId((normalized && normalized.userId) || "");
    setEmailAddress((normalized && normalized.emailAddress) || "");
    setTradingAccountNumber((normalized && normalized.tradingAccountNumber) || "");
  }, [open, override]);

  const handleSave = () =>
    onSave({
      userId,
      emailAddress,
      tradingAccountNumber,
    });

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Test Destination</DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" color="textSecondary">
          Leave every field blank to use your authenticated admin user by default.
          {allowEmailAddress
            ? " Saved overrides stay local to this browser for the current admin user and"
            : " For push tests, use a user ID or trading account number that already has a registered push device. Saved overrides stay local to this browser for the current admin user and"}
          server{server ? ` (${server})` : ""}.
        </Typography>
        <TextField
          id="test-destination-user-id"
          fullWidth
          variant="outlined"
          label="User ID"
          value={userId}
          onChange={(event) => setUserId(event.target.value)}
          className={classes.field}
          margin="normal"
        />
        {allowEmailAddress ? (
          <TextField
            id="test-destination-email"
            fullWidth
            variant="outlined"
            label="Email address"
            value={emailAddress}
            onChange={(event) => setEmailAddress(event.target.value)}
            className={classes.field}
          />
        ) : null}
        <TextField
          id="test-destination-trading-account"
          fullWidth
          variant="outlined"
          label="Trading account number"
          value={tradingAccountNumber}
          onChange={(event) => setTradingAccountNumber(event.target.value)}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          color="primary"
          variant="contained"
          onClick={handleSave}
          disabled={busy}
          data-testid="test-destination-save-button"
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const TestDestinationToolbar = ({
  override,
  busy,
  onOpen,
  onClear,
  buttonTestId,
  clearButtonTestId,
  children,
}) => {
  const classes = useStyles();
  const chipLabels = getTestDestinationChipLabels(override);

  return (
    <div className={classes.toolbarBlock}>
      <div className={classes.toolbarActions}>
        {children}
        <Button
          variant="outlined"
          color="primary"
          onClick={onOpen}
          disabled={busy}
          data-testid={buttonTestId}
        >
          Test Destination
        </Button>
        {chipLabels.length > 0 ? (
          <Button
            variant="outlined"
            onClick={onClear}
            disabled={busy}
            data-testid={clearButtonTestId}
          >
            Clear Overrides
          </Button>
        ) : null}
      </div>
      <div className={classes.toolbarSupport}>
        <Typography variant="body2" className={classes.toolbarNote}>
          Test sends default to your authenticated admin user when no override is saved.
        </Typography>
        <div className={classes.compactChipRow}>
          {chipLabels.length > 0 ? (
            chipLabels.map((chip) => <Chip key={chip.key} size="small" label={chip.label} />)
          ) : (
            <Chip size="small" label="Default destination: authenticated admin user" />
          )}
        </div>
      </div>
    </div>
  );
};

const PushTemplateCreateDialog = ({
  open,
  bindings,
  draft,
  brand,
  onBindingChange,
  onFieldChange,
  onClose,
  onSubmit,
  busy,
}) => {
  const classes = useStyles();
  const bindingSelected = Boolean(draft.bindingKey);
  const canSubmit =
    draft.templateKey &&
    draft.displayName &&
    draft.section &&
    (draft.title || draft.body);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Create Push Template</DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" color="textSecondary">
          Creates an English (US) push template for the selected brand and
          publishes it immediately so app senders can resolve it right away.
        </Typography>
        <TextField
          select
          fullWidth
          variant="outlined"
          label="Associate With App Push"
          value={draft.bindingKey}
          onChange={(event) => onBindingChange(event.target.value)}
          className={classes.field}
          id="push-create-binding"
        >
          <MenuItem value="">Custom / not yet bound</MenuItem>
          {(bindings || []).map((binding) => (
            <MenuItem key={binding.bindingKey} value={binding.bindingKey}>
              {binding.displayName} ({binding.section})
            </MenuItem>
          ))}
        </TextField>
        <TextField
          fullWidth
          variant="outlined"
          label="Template Key"
          value={draft.templateKey}
          onChange={(event) => onFieldChange("templateKey", event.target.value)}
          className={classes.field}
          id="push-create-template-key"
          disabled={bindingSelected}
        />
        <TextField
          fullWidth
          variant="outlined"
          label="Display Name"
          value={draft.displayName}
          onChange={(event) => onFieldChange("displayName", event.target.value)}
          className={classes.field}
          id="push-create-display-name"
        />
        <TextField
          fullWidth
          variant="outlined"
          label="Section"
          value={draft.section}
          onChange={(event) => onFieldChange("section", event.target.value)}
          className={classes.field}
          id="push-create-section"
        />
        <TextField
          fullWidth
          variant="outlined"
          label="Description"
          value={draft.description}
          onChange={(event) => onFieldChange("description", event.target.value)}
          className={classes.field}
          id="push-create-description"
        />
        <TextField
          fullWidth
          variant="outlined"
          label="Brand"
          value={brand}
          className={classes.field}
          InputProps={{
            readOnly: true,
          }}
          id="push-create-brand"
        />
        <TextField
          fullWidth
          variant="outlined"
          label="Language"
          value="English (US)"
          className={classes.field}
          InputProps={{
            readOnly: true,
          }}
          id="push-create-locale"
        />
        <TextField
          fullWidth
          variant="outlined"
          label="Push Title"
          value={draft.title}
          onChange={(event) => onFieldChange("title", event.target.value)}
          className={classes.field}
          id="push-create-title"
        />
        <TextField
          fullWidth
          multiline
          minRows={4}
          variant="outlined"
          label="Push Body"
          value={draft.body}
          onChange={(event) => onFieldChange("body", event.target.value)}
          className={classes.field}
          id="push-create-body"
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          color="primary"
          variant="contained"
          onClick={onSubmit}
          disabled={busy || !canSubmit}
          data-testid="push-create-submit-button"
        >
          {busy ? "Creating..." : "Create Template"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const ImagePickerDialog = ({ open, onClose, onInsertReference }) => {
  const classes = useStyles();
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");
  const [error, setError] = useState("");

  const loadAssets = async () => {
    setLoading(true);
    setError("");

    try {
      const response = await api.get("/api/v2/ServiceCenter/notification-templates/images", {
        params: {
          search,
          includeArchived: false,
        },
      });

      setAssets((response.data && response.data.items) || []);
    } catch (loadError) {
      setError("Unable to load template images right now.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!open) {
      return;
    }

    loadAssets();
  }, [open]);

  const handleCopy = async (value, label) => {
    try {
      await copyToClipboard(value);
      setStatus(`${label} copied.`);
    } catch (copyError) {
      setError(`Unable to copy the ${label.toLowerCase()}.`);
    }
  };

  const handleInsert = (value, label) => {
    onInsertReference(value);
    setStatus(`${label} inserted.`);
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>Image Library</DialogTitle>
      <DialogContent dividers>
        <StatusBanners status={status} error={error} />
        <div className={classes.selectRow}>
          <TextField
            fullWidth
            variant="outlined"
            label="Search images"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
          />
          <Button variant="outlined" onClick={loadAssets} disabled={loading}>
            Refresh
          </Button>
        </div>

        {loading ? (
          <div className={classes.loadingWrap}>
            <CircularProgress />
          </div>
        ) : assets.length === 0 ? (
          <div className={classes.emptyState}>
            <Typography variant="subtitle1">No images found</Typography>
            <Typography variant="body2">
              Use the Images tab to upload shared email assets.
            </Typography>
          </div>
        ) : (
          <Grid container spacing={2}>
            {assets.map((asset) => (
              <Grid item xs={12} md={6} key={asset.notificationImageAssetId}>
                <Paper className={classes.imageCard}>
                  <img
                    src={asset.url}
                    alt={asset.altText || asset.displayName}
                    className={classes.imagePreview}
                  />
                  <Typography variant="subtitle1" className={classes.rowTitle}>
                    {asset.displayName}
                  </Typography>
                  <Typography className={classes.rowKey}>{asset.fileName}</Typography>
                  <div className={classes.chipRow}>
                    {(asset.brands || []).map((brand) => (
                      <Chip key={`${asset.notificationImageAssetId}-${brand}`} size="small" label={brand} />
                    ))}
                    {asset.isArchived ? <Chip size="small" color="secondary" label="Archived" /> : null}
                  </div>
                  <div className={classes.actionRow}>
                    <Button
                      variant="outlined"
                      onClick={() => handleCopy(asset.url, "URL")}
                      data-testid={`picker-copy-url-${asset.notificationImageAssetId}`}
                    >
                      Copy URL
                    </Button>
                    <Button
                      variant="outlined"
                      onClick={() => handleCopy(asset.htmlSnippet, "HTML")}
                      data-testid={`picker-copy-html-${asset.notificationImageAssetId}`}
                    >
                      Copy HTML
                    </Button>
                    <Button
                      variant="outlined"
                      color="primary"
                      onClick={() => handleInsert(asset.url, "URL")}
                      data-testid={`picker-insert-url-${asset.notificationImageAssetId}`}
                    >
                      Insert URL
                    </Button>
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={() => handleInsert(asset.htmlSnippet, "HTML")}
                      data-testid={`picker-insert-html-${asset.notificationImageAssetId}`}
                    >
                      Insert HTML
                    </Button>
                  </div>
                </Paper>
              </Grid>
            ))}
          </Grid>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

const ImageUsageDialog = ({ open, onClose, usage }) => {
  const classes = useStyles();

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>Image Usage</DialogTitle>
      <DialogContent dividers>
        {!usage ? (
          <Typography variant="body2" color="textSecondary">
            No usage data loaded.
          </Typography>
        ) : (
          <>
            <div className={classes.editorMeta}>
              <Chip label={`Published: ${usage.publishedUsageCount || 0}`} />
              <Chip label={`Draft: ${usage.draftUsageCount || 0}`} />
            </div>
            <Typography variant="subtitle1" className={classes.sectionTitle}>
              Published References
            </Typography>
            <List className={classes.usageList}>
              {(usage.publishedTemplates || []).map((item) => (
                <ListItem key={`${item.templateKind}-${item.templateKey}-${item.brand}-${item.locale}`}>
                  <Typography variant="body2">
                    {item.displayName} ({item.templateKind}) - {item.brand} / {item.locale}
                  </Typography>
                </ListItem>
              ))}
            </List>
            <Typography variant="subtitle1" className={classes.sectionTitle}>
              Draft References
            </Typography>
            <List className={classes.usageList}>
              {(usage.draftTemplates || []).map((item) => (
                <ListItem key={`${item.templateKind}-${item.templateKey}-${item.brand}-${item.locale}`}>
                  <Typography variant="body2">
                    {item.displayName} ({item.templateKind}) - {item.brand} / {item.locale}
                  </Typography>
                </ListItem>
              ))}
            </List>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

const NotificationNav = ({ rootMatch }) => {
  const classes = useStyles();
  const history = useHistory();
  const location = useLocation();

  const value = useMemo(() => {
    if (location.pathname.indexOf("/email") >= 0) {
      return "email";
    }

    if (location.pathname.indexOf("/migration") >= 0) {
      return "migration";
    }

    if (location.pathname.indexOf("/images") >= 0) {
      return "images";
    }

    return "push";
  }, [location.pathname]);

  return (
    <Paper className={classes.navTabs}>
      <Tabs
        value={value}
        onChange={(_, nextValue) => history.push(`${rootMatch.url}/${nextValue}`)}
        indicatorColor="primary"
        textColor="primary"
        variant="scrollable"
        scrollButtons="auto"
      >
        <Tab value="push" label="Push Templates" />
        <Tab value="email" label="Email Templates" />
        <Tab value="migration" label="Migration Report" />
        <Tab value="images" label="Image Library" />
      </Tabs>
    </Paper>
  );
};

const NotificationScaffold = ({ match, rootMatch, heading, subtitle, children }) => {
  const classes = useStyles();

  return (
    <div className="app-wrapper">
      <ContainerHeader match={match} title="Notification Templates" />
      <div className={classes.page}>
        <NotificationNav rootMatch={rootMatch} />
        <Paper className={classes.shell}>
          <div className={classes.toolbar}>
            <div>
              <Typography variant="h5" className={classes.title}>
                {heading}
              </Typography>
              <Typography variant="body1" className={classes.subtitle}>
                {subtitle}
              </Typography>
            </div>
          </div>
          {children}
        </Paper>
      </div>
    </div>
  );
};

const PushTemplatesPage = ({ match, rootMatch }) => {
  const classes = useStyles();
  const history = useHistory();
  const location = useLocation();
  const [sections, setSections] = useState([]);
  const [bindings, setBindings] = useState([]);
  const [locales, setLocales] = useState([]);
  const [loadingList, setLoadingList] = useState(true);
  const [loadingEditor, setLoadingEditor] = useState(false);
  const [saving, setSaving] = useState(false);
  const [working, setWorking] = useState(false);
  const [search, setSearch] = useState("");
  const [lockFilter, setLockFilter] = useState("all");
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [activeBrand, setActiveBrand] = useState(BRANDS[0]);
  const [selectedLocale, setSelectedLocale] = useState(DEFAULT_LOCALE);
  const [editor, setEditor] = useState(null);
  const [previewJson, setPreviewJson] = useState("{}");
  const [previewState, setPreviewState] = useState("Draft");
  const [selectedRevision, setSelectedRevision] = useState(null);
  const [status, setStatus] = useState("");
  const [error, setError] = useState("");
  const [translationDialogOpen, setTranslationDialogOpen] = useState(false);
  const [translationLocales, setTranslationLocales] = useState([]);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [testDestinationDialogOpen, setTestDestinationDialogOpen] = useState(false);
  const [createDraft, setCreateDraft] = useState(createEmptyPushTemplateDraft());
  const [compareSource, setCompareSource] = useState("draft");
  const [compareTarget, setCompareTarget] = useState("published");
  const [dependencyState, setDependencyState] = useState("Draft");
  const [dependencyGraph, setDependencyGraph] = useState(null);
  const [loadingDependencies, setLoadingDependencies] = useState(false);
  const {
    override: testDestinationOverride,
    hasOverride: hasTestDestinationOverride,
    saveOverride: saveTestDestinationOverride,
    clearOverride: clearTestDestinationOverride,
    server: testDestinationServer,
  } = useStoredTestDestinationOverride();

  const loadSections = async () => {
    setLoadingList(true);
    try {
      const response = await api.get(
        "/api/v2/ServiceCenter/notification-templates/push/sections"
      );
      setSections(response.data || []);
    } catch (loadError) {
      setError("Unable to load push templates right now.");
    } finally {
      setLoadingList(false);
    }
  };

  const loadLocales = async () => {
    try {
      const response = await api.get(
        "/api/v2/ServiceCenter/notification-templates/locales"
      );
      setLocales(response.data || []);
    } catch (loadError) {
      setLocales([]);
    }
  };

  const loadBindings = async () => {
    try {
      const response = await api.get(
        "/api/v2/ServiceCenter/notification-templates/push/bindings"
      );
      setBindings(response.data || []);
    } catch (loadError) {
      setBindings([]);
    }
  };

  useEffect(() => {
    loadSections();
    loadLocales();
    loadBindings();
  }, []);

  useEffect(() => {
    const jump = location.state && location.state.notificationJump;
    if (!jump || jump.templateKind !== "PushTemplate" || !jump.templateKey) {
      return;
    }

    loadEditor(
      { templateKey: jump.templateKey, locales: [jump.locale || DEFAULT_LOCALE] },
      jump.brand || activeBrand,
      jump.locale || DEFAULT_LOCALE
    );
    history.replace(location.pathname);
  }, [activeBrand, history, location.pathname, location.state]);

  const visibleGroups = useMemo(
    () => filterGroups(sections, search, lockFilter),
    [sections, search, lockFilter]
  );

  const visibleTemplateKeys = useMemo(
    () => collectVisibleTemplateKeys(visibleGroups),
    [visibleGroups]
  );

  const loadEditor = async (template, brand, locale) => {
    setLoadingEditor(true);
    setError("");
    setSelectedRevision(null);
    setCompareTarget("published");
    setDependencyGraph(null);

    try {
      const response = await api.get(
        `/api/v2/ServiceCenter/notification-templates/push/${template.templateKey}`,
        {
          params: {
            brand,
            locale,
          },
        }
      );

      setSelectedTemplate(template);
      setActiveBrand(brand);
      setSelectedLocale(locale);
      setEditor(response.data);
      setPreviewJson(
        buildSampleParameterJson(
          ((response.data && response.data.draft && response.data.draft.allowedTokens) ||
            (response.data &&
              response.data.published &&
              response.data.published.allowedTokens) ||
            [])
        )
      );
    } catch (loadError) {
      setError(getApiErrorMessage(loadError, "Unable to load the push template editor."));
    } finally {
      setLoadingEditor(false);
    }
  };

  const handleSelectTemplate = async (template) => {
    const locale = getFirstLocale(template.locales);
    await loadEditor(template, activeBrand, locale);
  };

  const handleBrandChange = async (_, nextBrand) => {
    if (!selectedTemplate) {
      setActiveBrand(nextBrand);
      return;
    }

    await loadEditor(selectedTemplate, nextBrand, selectedLocale);
  };

  const handleLocaleChange = async (event) => {
    const nextLocale = event.target.value;
    if (!selectedTemplate) {
      setSelectedLocale(nextLocale);
      return;
    }

    await loadEditor(selectedTemplate, activeBrand, nextLocale);
  };

  const updateDraftField = (field, value) => {
    setEditor((current) => ({
      ...current,
      draft: {
        ...(current && current.draft ? current.draft : {}),
        [field]: value,
      },
    }));
  };

  const handleSaveDraft = async () => {
    if (!selectedTemplate || !editor) {
      return;
    }

    setSaving(true);
    setStatus("");
    setError("");

    try {
      const response = await api.put(
        `/api/v2/ServiceCenter/notification-templates/push/${selectedTemplate.templateKey}`,
        {
          title: editor.draft && editor.draft.title,
          body: editor.draft && editor.draft.body,
        },
        {
          params: {
            brand: activeBrand,
            locale: selectedLocale,
          },
        }
      );

      setEditor(response.data);
      setStatus("Push draft saved.");
      await loadSections();
    } catch (saveError) {
      setError("Unable to save the push draft.");
    } finally {
      setSaving(false);
    }
  };

  const handlePublish = async () => {
    if (!selectedTemplate) {
      return;
    }

    setWorking(true);
    setStatus("");
    setError("");

    try {
      await api.post(
        `/api/v2/ServiceCenter/notification-templates/push/${selectedTemplate.templateKey}/publish`,
        null,
        {
          params: {
            brand: activeBrand,
            locale: selectedLocale,
          },
        }
      );

      setStatus("Push draft published.");
      await loadSections();
      await loadEditor(selectedTemplate, activeBrand, selectedLocale);
    } catch (publishError) {
      setError("Unable to publish the push draft.");
    } finally {
      setWorking(false);
    }
  };

  const handleSaveTestDestination = (nextOverride) => {
    const normalized = saveTestDestinationOverride(nextOverride);
    setStatus(
      normalized
        ? "Test destination overrides saved locally."
        : "Test destination overrides cleared."
    );
    setError("");
    setTestDestinationDialogOpen(false);
  };

  const handleClearTestDestination = () => {
    clearTestDestinationOverride();
    setStatus("Test destination overrides cleared.");
    setError("");
  };

  const handleSendTestPush = async () => {
    if (!selectedTemplate) {
      return;
    }

    setWorking(true);
    setStatus("");
    setError("");

    try {
      const response = await api.post(
        `/api/v2/ServiceCenter/notification-templates/push/${selectedTemplate.templateKey}/test-send`,
        buildTestSendRequestBody(
          previewJson,
          previewState,
          hasTestDestinationOverride ? testDestinationOverride : null
        ),
        {
          params: {
            brand: activeBrand,
            locale: selectedLocale,
          },
        }
      );

      setStatus(`Test push sent to ${describeResolvedTestDestination(response.data)}.`);
    } catch (sendError) {
      setError(
        getApiErrorMessage(
          sendError,
          "Unable to send the push test notification. Check the sample token JSON."
        )
      );
    } finally {
      setWorking(false);
    }
  };

  const handleLoadRevision = async (revisionId) => {
    if (!selectedTemplate) {
      return;
    }

    setWorking(true);
    setError("");

    try {
      const response = await api.get(
        `/api/v2/ServiceCenter/notification-templates/push/${selectedTemplate.templateKey}/history/${revisionId}`,
        {
          params: {
            brand: activeBrand,
            locale: selectedLocale,
          },
        }
      );

      setSelectedRevision(response.data);
    } catch (loadError) {
      setError("Unable to load that revision.");
    } finally {
      setWorking(false);
    }
  };

  const handleRestoreRevision = async (revisionId) => {
    if (!selectedTemplate) {
      return;
    }

    setWorking(true);
    setStatus("");
    setError("");

    try {
      const response = await api.post(
        `/api/v2/ServiceCenter/notification-templates/push/${selectedTemplate.templateKey}/history/${revisionId}/restore`,
        null,
        {
          params: {
            brand: activeBrand,
            locale: selectedLocale,
          },
        }
      );

      setEditor(response.data);
      setStatus("Revision restored into draft.");
      setSelectedRevision(null);
      await loadSections();
    } catch (restoreError) {
      setError("Unable to restore that revision.");
    } finally {
      setWorking(false);
    }
  };

  const handleToggleLock = async (isLocked) => {
    if (!selectedTemplate || !editor) {
      return;
    }

    setWorking(true);
    setStatus("");
    setError("");

    try {
      await api.post(
        `/api/v2/ServiceCenter/notification-templates/push/${selectedTemplate.templateKey}/${isLocked ? "unlock" : "lock"}`,
        null,
        {
          params: {
            brand: activeBrand,
            locale: selectedLocale,
          },
        }
      );

      setStatus(isLocked ? "Language unlocked." : "Language locked.");
      await loadSections();
      await loadEditor(selectedTemplate, activeBrand, selectedLocale);
    } catch (lockError) {
      setError("Unable to update the lock state.");
    } finally {
      setWorking(false);
    }
  };

  const handleUnlockAll = async () => {
    if (visibleTemplateKeys.length === 0) {
      return;
    }

    setWorking(true);
    setStatus("");
    setError("");

    try {
      const response = await api.post(
        "/api/v2/ServiceCenter/notification-templates/push/unlock-all",
        {
          templateKeys: visibleTemplateKeys,
          templateKind: "PushTemplate",
        }
      );

      setStatus(`Unlocked ${response.data.unlockedCount || 0} push language records.`);
      await loadSections();
      if (selectedTemplate) {
        await loadEditor(selectedTemplate, activeBrand, selectedLocale);
      }
    } catch (unlockError) {
      setError("Unable to unlock the filtered push templates.");
    } finally {
      setWorking(false);
    }
  };

  const toggleTranslationLocale = (locale) => {
    setTranslationLocales((current) =>
      current.indexOf(locale) >= 0
        ? current.filter((item) => item !== locale)
        : current.concat(locale)
    );
  };

  const handleQueueTranslations = async () => {
    if (visibleTemplateKeys.length === 0 || translationLocales.length === 0) {
      return;
    }

    setWorking(true);
    setStatus("");
    setError("");

    try {
      const response = await api.post(
        "/api/v2/ServiceCenter/notification-templates/translations/jobs",
        {
          channel: "push",
          templateKind: "PushTemplate",
          templateKeys: visibleTemplateKeys,
          targetLocales: translationLocales,
          brands: BRANDS,
        }
      );

      setStatus(
        `Queued push translations for ${response.data.templateCount || 0} templates.`
      );
      setTranslationDialogOpen(false);
      setTranslationLocales([]);
    } catch (jobError) {
      setError("Unable to queue the translation job.");
    } finally {
      setWorking(false);
    }
  };

  const handleCompareTargetChange = async (nextTarget) => {
    setCompareTarget(nextTarget);

    if (nextTarget.indexOf("revision:") !== 0) {
      return;
    }

    await handleLoadRevision(nextTarget.replace("revision:", ""));
  };

  const handleSaveSchedule = async (runAtValue) => {
    if (!selectedTemplate) {
      return;
    }

    const runAtUtc = parseDateTimeInput(runAtValue);
    if (!runAtUtc) {
      setError("Choose a valid scheduled publish time.");
      return;
    }

    setWorking(true);
    setStatus("");
    setError("");

    try {
      const response = await api.put(
        `/api/v2/ServiceCenter/notification-templates/push/${selectedTemplate.templateKey}/schedule`,
        {
          runAtUtc,
        },
        {
          params: {
            brand: activeBrand,
            locale: selectedLocale,
          },
        }
      );

      setEditor((current) => ({
        ...current,
        scheduledPublish: response.data,
      }));
      setStatus("Push draft scheduled for publish.");
    } catch (scheduleError) {
      setError(getApiErrorMessage(scheduleError, "Unable to schedule the push draft."));
    } finally {
      setWorking(false);
    }
  };

  const handleCancelSchedule = async () => {
    if (!selectedTemplate) {
      return;
    }

    setWorking(true);
    setStatus("");
    setError("");

    try {
      await api.delete(
        `/api/v2/ServiceCenter/notification-templates/push/${selectedTemplate.templateKey}/schedule`,
        {
          params: {
            brand: activeBrand,
            locale: selectedLocale,
          },
        }
      );

      setEditor((current) => ({
        ...current,
        scheduledPublish: null,
      }));
      setStatus("Push schedule canceled.");
    } catch (cancelError) {
      setError(getApiErrorMessage(cancelError, "Unable to cancel the push schedule."));
    } finally {
      setWorking(false);
    }
  };

  const loadDependencies = async (nextState = dependencyState) => {
    if (!selectedTemplate) {
      return;
    }

    setLoadingDependencies(true);
    setError("");

    try {
      const response = await api.get(
        `/api/v2/ServiceCenter/notification-templates/push/${selectedTemplate.templateKey}/dependencies`,
        {
          params: {
            brand: activeBrand,
            locale: selectedLocale,
            state: nextState,
          },
        }
      );

      setDependencyGraph(response.data || null);
    } catch (dependencyError) {
      setError(getApiErrorMessage(dependencyError, "Unable to load push dependencies."));
    } finally {
      setLoadingDependencies(false);
    }
  };

  const handleDependencyNodeClick = async (node) => {
    if (!node) {
      return;
    }

    if (node.templateKind === "PushTemplate" && node.templateKey) {
      await loadEditor(
        { templateKey: node.templateKey, locales: [node.locale || DEFAULT_LOCALE] },
        node.brand || activeBrand,
        node.locale || selectedLocale
      );
      return;
    }

    if (node.templateKey && node.templateKind) {
      history.push(`${rootMatch.url}/email`, {
        notificationJump: {
          templateKind: node.templateKind,
          templateKey: node.templateKey,
          brand: node.brand,
          locale: node.locale,
        },
      });
    }
  };

  const resetCreateDraft = (bindingKey) => {
    const binding = (bindings || []).find((item) => item.bindingKey === bindingKey);
    setCreateDraft(binding ? applyPushBindingToDraft(binding) : createEmptyPushTemplateDraft());
  };

  const handleOpenCreateDialog = () => {
    resetCreateDraft((bindings && bindings[0] && bindings[0].bindingKey) || "");
    setCreateDialogOpen(true);
  };

  const handleCreateBindingChange = (bindingKey) => {
    resetCreateDraft(bindingKey);
  };

  const handleCreateFieldChange = (field, value) => {
    setCreateDraft((current) => ({
      ...current,
      [field]: value,
    }));
  };

  const handleCreateTemplate = async () => {
    setWorking(true);
    setStatus("");
    setError("");

    try {
      const selectedBinding = bindings.find(
        (binding) => binding.bindingKey === createDraft.bindingKey
      );
      const existingTemplate = selectedBinding
        ? findPushTemplateInSections(sections, selectedBinding.templateKey)
        : null;

      if (selectedBinding && existingTemplate) {
        const locale = getFirstLocale(existingTemplate.locales);
        const brand = getPreferredBrand(existingTemplate.brands, activeBrand);
        setCreateDialogOpen(false);
        setCreateDraft(createEmptyPushTemplateDraft());
        setStatus("That app push already has a template. Opened it for editing.");
        await loadEditor(existingTemplate, brand, locale);
        return;
      }

      const response = await api.post(
        "/api/v2/ServiceCenter/notification-templates/push",
        {
          bindingKey: createDraft.bindingKey || null,
          templateKey: createDraft.templateKey,
          displayName: createDraft.displayName,
          section: createDraft.section,
          description: createDraft.description,
          title: createDraft.title,
          body: createDraft.body,
        },
        {
          params: {
            brand: activeBrand,
            locale: DEFAULT_LOCALE,
          },
        }
      );

      const createdEditor = response.data;
      setCreateDialogOpen(false);
      setCreateDraft(createEmptyPushTemplateDraft());
      setStatus("Push template is ready to edit.");
      await loadSections();
      await loadEditor(
        {
          templateKey: createdEditor.templateKey,
          locales: [createdEditor.locale || DEFAULT_LOCALE],
        },
        createdEditor.brand || activeBrand,
        createdEditor.locale || DEFAULT_LOCALE
      );
    } catch (createError) {
      setError(getApiErrorMessage(createError, "Unable to create the push template."));
    } finally {
      setWorking(false);
    }
  };

  const pushPreviewTokens = collectAllowedTokens(
    editor && editor.draft && editor.draft.allowedTokens,
    editor && editor.published && editor.published.allowedTokens
  );
  const pushPreviewTokenValues = buildTokenInputValues(
    pushPreviewTokens,
    previewJson
  );
  const previewJsonIsInvalid =
    Boolean(previewJson && previewJson.trim()) &&
    tryParseParameterJson(previewJson) === null;
  const pushDestinationChipLabels = getTestDestinationChipLabels(
    testDestinationOverride
  );
  const compareTargetOptions = createHistoryCompareOptions(editor && editor.history);
  const compareTargetLabel = getCompareTargetLabel(compareTarget, compareTargetOptions);
  const compareSourceVersion =
    compareSource === "published"
      ? editor && editor.published
      : editor && editor.draft;
  const compareTargetVersion =
    compareTarget.indexOf("revision:") === 0
      ? selectedRevision && selectedRevision.push
      : editor && editor.published;
  const filteredDependencyGraph = removeImageReferencesFromDependencyGraph(
    dependencyGraph
  );

  return (
    <NotificationScaffold
      match={match}
      rootMatch={rootMatch}
      heading="Push Template Editor"
      subtitle="Drafts stay safe until publish. Use test sends to validate tokenized copy before you push it into the runtime cache."
    >
      <StatusBanners status={status} error={error} />

      <TestDestinationToolbar
        override={testDestinationOverride}
        busy={working}
        onOpen={() => setTestDestinationDialogOpen(true)}
        onClear={handleClearTestDestination}
        buttonTestId="push-test-destination-button"
        clearButtonTestId="push-clear-test-destination-button"
      >
        <Button
          variant="contained"
          color="primary"
          onClick={handleOpenCreateDialog}
          disabled={working}
          data-testid="push-create-template-button"
        >
          Create Push Template
        </Button>
        <Button
          variant="outlined"
          color="primary"
          onClick={() => setTranslationDialogOpen(true)}
          disabled={visibleTemplateKeys.length === 0 || working}
        >
          Add Languages
        </Button>
        <Button
          variant="outlined"
          onClick={handleUnlockAll}
          disabled={visibleTemplateKeys.length === 0 || working}
        >
          Unlock All
        </Button>
      </TestDestinationToolbar>

      <Grid container spacing={4}>
        <Grid item xs={12} md={4}>
          <Paper className={classes.listPanel}>
            <TextField
              fullWidth
              variant="outlined"
              label="Search push templates"
              placeholder="ManualTradeFillPush, trading, banking..."
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              className={classes.searchField}
            />

            <div className={classes.filterRow}>
              {LOCK_FILTERS.map((option) => (
                <Chip
                  key={option.value}
                  clickable
                  color={lockFilter === option.value ? "primary" : "default"}
                  label={option.label}
                  onClick={() => setLockFilter(option.value)}
                />
              ))}
            </div>

            {loadingList ? (
              <div className={classes.loadingWrap}>
                <CircularProgress />
              </div>
            ) : visibleGroups.length === 0 ? (
              <div className={classes.emptyState}>
                <Typography variant="subtitle1">No push templates found</Typography>
                <Typography variant="body2">
                  Try a different search or filter combination.
                </Typography>
              </div>
            ) : (
              visibleGroups.map((group) => (
                <Paper key={group.section} className={classes.groupCard}>
                  <div className={classes.groupHeader}>{group.section}</div>
                  <List className={classes.list}>
                    {(group.templates || []).map((template, index) => (
                      <React.Fragment key={template.templateKey}>
                        <ListItem
                          button
                          alignItems="flex-start"
                          className={classes.row}
                          onClick={() => handleSelectTemplate(template)}
                          data-testid={`push-template-row-${template.templateKey}`}
                        >
                          <div className={classes.rowInner}>
                            <div>
                              <Typography variant="subtitle1" className={classes.rowTitle}>
                                {template.displayName}
                              </Typography>
                              <Typography className={classes.rowKey}>
                                {template.templateKey}
                              </Typography>
                              <Typography variant="body2" className={classes.rowDescription}>
                                {template.description}
                              </Typography>
                              <div className={classes.chipRow}>
                                <Chip size="small" label={`Locales: ${(template.locales || []).join(", ")}`} />
                                <Chip size="small" label={`Brands: ${(template.brands || []).join(", ")}`} />
                                {template.staleLocaleCount ? (
                                  <Chip size="small" color="secondary" label={`Stale locales: ${template.staleLocaleCount}`} />
                                ) : null}
                                {template.missingLocaleCount ? (
                                  <Chip size="small" label={`Missing locales: ${template.missingLocaleCount}`} />
                                ) : null}
                                {template.hasDraftChanges ? (
                                  <Chip size="small" color="primary" label="Draft changes" />
                                ) : null}
                                <Chip
                                  size="small"
                                  color={template.hasLockedLocales ? "secondary" : "default"}
                                  label={template.hasLockedLocales ? "Locked locales" : "Unlocked"}
                                />
                              </div>
                            </div>
                            <Typography variant="body2" className={classes.rowMeta}>
                              {formatDate(template.lastPublishedUtc || template.lastUpdatedUtc)}
                            </Typography>
                          </div>
                        </ListItem>
                        {index < group.templates.length - 1 ? <Divider /> : null}
                      </React.Fragment>
                    ))}
                  </List>
                </Paper>
              ))
            )}
          </Paper>
        </Grid>

        <Grid item xs={12} md={8}>
          <Paper className={classes.editorPanel}>
            {!selectedTemplate ? (
              <div className={classes.emptyState}>
                <Typography variant="h6">Select a push template</Typography>
                <Typography variant="body2">
                  Choose a template from the left to edit its draft, send a test
                  push, and publish it when ready.
                </Typography>
              </div>
            ) : loadingEditor || !editor ? (
              <div className={classes.loadingWrap}>
                <CircularProgress />
              </div>
            ) : (
              <>
                <div className={classes.editorHeader}>
                  <Typography variant="h6" className={classes.sectionTitle}>
                    {editor.displayName}
                  </Typography>
                  <div className={classes.editorMeta}>
                    <Chip label={`Section: ${editor.section}`} />
                    <Chip label={`Provenance: ${editor.provenance && editor.provenance.value}`} />
                    <Chip label={`Draft updated: ${formatDate(editor.draftAudit && editor.draftAudit.updatedDateUtc)}`} />
                    <Chip label={`Published: ${formatDate(editor.publishAudit && editor.publishAudit.updatedDateUtc)}`} />
                    <Chip
                      color={editor.isLocked ? "secondary" : "default"}
                      label={editor.isLocked ? "Locked" : "Unlocked"}
                    />
                    {editor.hasDraftChanges ? <Chip color="primary" label="Unpublished draft" /> : null}
                  </div>
                </div>

                <div className={classes.brandLocaleRow}>
                  <Tabs
                    value={activeBrand}
                    onChange={handleBrandChange}
                    indicatorColor="primary"
                    textColor="primary"
                    className={classes.brandTabs}
                  >
                    {BRANDS.map((brand) => (
                      <Tab key={brand} value={brand} label={brand} />
                    ))}
                  </Tabs>

                  <FormControl
                    variant="outlined"
                    className={`${classes.selectField} ${classes.localeControl}`}
                  >
                    <InputLabel id="push-language-select-label">Language</InputLabel>
                    <Select
                      labelId="push-language-select-label"
                      value={selectedLocale}
                      onChange={handleLocaleChange}
                      label="Language"
                    >
                      {(editor.availableLocales || []).map((locale) => {
                        const localeOption = (locales || []).find((item) => item.locale === locale);

                        return (
                          <MenuItem key={locale} value={locale}>
                            {localeOption ? localeOption.displayName : locale}
                          </MenuItem>
                        );
                      })}
                    </Select>
                  </FormControl>
                </div>

                <Grid container spacing={4}>
                  <Grid item xs={12} md={6}>
                    <Paper className={classes.versionCard}>
                      <Typography variant="subtitle1" className={classes.sectionTitle}>
                        Draft
                      </Typography>
                      <TextField
                        id="push-template-title"
                        fullWidth
                        variant="outlined"
                        label="Draft Title"
                        value={(editor.draft && editor.draft.title) || ""}
                        onChange={(event) => updateDraftField("title", event.target.value)}
                        className={classes.field}
                      />
                      <TextField
                        id="push-template-body"
                        fullWidth
                        multiline
                        minRows={5}
                        variant="outlined"
                        label="Draft Body"
                        value={(editor.draft && editor.draft.body) || ""}
                        onChange={(event) => updateDraftField("body", event.target.value)}
                        className={classes.field}
                      />
                      <Typography variant="subtitle2">Detected Tokens</Typography>
                      <div className={classes.tokenList}>
                        {((editor.draft && editor.draft.allowedTokens) || []).length > 0 ? (
                          (editor.draft.allowedTokens || []).map((token) => (
                            <Chip key={token} size="small" label={`{${token}}`} />
                          ))
                        ) : (
                          <Typography variant="body2" color="textSecondary">
                            No placeholders detected.
                          </Typography>
                        )}
                      </div>
                    </Paper>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Paper className={classes.versionCard}>
                      <Typography variant="subtitle1" className={classes.sectionTitle}>
                        Published
                      </Typography>
                      <Typography variant="subtitle2">Title</Typography>
                      <Typography variant="body2">
                        {(editor.published && editor.published.title) || "No published title."}
                      </Typography>
                      <Box mt={2}>
                        <Typography variant="subtitle2">Body</Typography>
                        <Typography variant="body2">
                          {(editor.published && editor.published.body) || "No published body."}
                        </Typography>
                      </Box>
                      <Box mt={2}>
                        <Typography variant="subtitle2">Tokens</Typography>
                        <div className={classes.tokenList}>
                          {((editor.published && editor.published.allowedTokens) || []).length > 0 ? (
                            (editor.published.allowedTokens || []).map((token) => (
                              <Chip key={token} size="small" label={`{${token}}`} />
                            ))
                          ) : (
                            <Typography variant="body2" color="textSecondary">
                              No placeholders detected.
                            </Typography>
                          )}
                        </div>
                      </Box>
                    </Paper>
                  </Grid>
                </Grid>

                <div className={classes.actionRow}>
                  <Button
                    color="primary"
                    variant="contained"
                    onClick={handleSaveDraft}
                    disabled={saving || working}
                    data-testid="push-save-draft-button"
                  >
                    {saving ? "Saving..." : "Save Draft"}
                  </Button>
                  <Button
                    variant="contained"
                    color="secondary"
                    onClick={handlePublish}
                    disabled={saving || working}
                    data-testid="push-publish-button"
                  >
                    {working ? "Publishing..." : "Publish"}
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={() => handleToggleLock(editor.isLocked)}
                    disabled={saving || working}
                  >
                    {editor.isLocked ? "Unlock" : "Lock"}
                  </Button>
                </div>

                <Box className={classes.sectionBlock}>
                  <Typography variant="subtitle1" className={classes.sectionTitle}>
                    Test Send Sandbox
                  </Typography>
                  <Grid container spacing={3}>
                    <Grid item xs={12} md={4}>
                      <FormControl variant="outlined" className={classes.selectField}>
                        <InputLabel id="push-preview-state-label">Send State</InputLabel>
                        <Select
                          labelId="push-preview-state-label"
                          value={previewState}
                          onChange={(event) => setPreviewState(event.target.value)}
                          label="Send State"
                        >
                          {PREVIEW_STATES.map((option) => (
                            <MenuItem key={option.value} value={option.value}>
                              {option.label}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} md={8}>
                      <div className={classes.destinationCard}>
                        <Typography variant="subtitle2">Test Destination</Typography>
                        <div className={classes.compactChipRow}>
                          {pushDestinationChipLabels.length > 0 ? (
                            pushDestinationChipLabels.map((chip) => (
                              <Chip key={chip.key} size="small" label={chip.label} />
                            ))
                          ) : (
                            <Chip size="small" label="Authenticated admin user" />
                          )}
                        </div>
                        <Typography variant="body2" className={classes.destinationHelp}>
                          Template test sends default to your authenticated admin user.
                          To mirror Service Center push dispatch, save a Test Destination
                          override with a user ID or trading account number that has a
                          registered push device.
                        </Typography>
                      </div>
                    </Grid>
                    <Grid item xs={12}>
                      <Typography variant="subtitle2">Token Values</Typography>
                      {pushPreviewTokens.length === 0 ? (
                        <Typography variant="body2" color="textSecondary">
                          No token variables detected for this template.
                        </Typography>
                      ) : (
                        <Grid container spacing={2}>
                          {pushPreviewTokens.map((token) => (
                            <Grid item xs={12} sm={6} md={4} key={token}>
                              <TextField
                                id={`push-preview-token-${token}`}
                                fullWidth
                                variant="outlined"
                                label={token}
                                value={pushPreviewTokenValues[token] || ""}
                                onChange={(event) =>
                                  setPreviewJson((current) =>
                                    updateTokenValueInJson(
                                      current,
                                      token,
                                      event.target.value
                                    )
                                  )
                                }
                                helperText={`Replaces {${token}}`}
                              />
                            </Grid>
                          ))}
                        </Grid>
                      )}
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        id="push-preview-json"
                        fullWidth
                        multiline
                        minRows={6}
                        variant="outlined"
                        label="Sample token JSON (Advanced)"
                        value={previewJson}
                        onChange={(event) => setPreviewJson(event.target.value)}
                      />
                      <Typography variant="body2" color={previewJsonIsInvalid ? "error" : "textSecondary"}>
                        {previewJsonIsInvalid
                          ? "Sample token JSON is invalid. Updating a token field above will repair it."
                          : "Token fields above keep this JSON in sync. Use the JSON editor for advanced payloads."}
                      </Typography>
                    </Grid>
                  </Grid>
                  <div className={classes.actionRow}>
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={handleSendTestPush}
                      disabled={working}
                      data-testid="push-test-send-button"
                    >
                      Send Test Push
                    </Button>
                  </div>
                </Box>

                <Box className={classes.sectionBlock}>
                  <NotificationChangesPanel
                    title="Push Changes"
                    compareSource={compareSource}
                    compareTarget={compareTarget}
                    compareTargetOptions={compareTargetOptions}
                    onCompareSourceChange={setCompareSource}
                    onCompareTargetChange={handleCompareTargetChange}
                    leftLabel={compareSource === "published" ? "Published" : "Draft"}
                    rightLabel={compareTargetLabel}
                    fields={[
                      {
                        label: "Title",
                        leftValue: compareSourceVersion && compareSourceVersion.title,
                        rightValue: compareTargetVersion && compareTargetVersion.title,
                      },
                      {
                        label: "Body",
                        leftValue: compareSourceVersion && compareSourceVersion.body,
                        rightValue: compareTargetVersion && compareTargetVersion.body,
                      },
                    ]}
                    metadata={[
                      {
                        label: "Tokens",
                        leftValue:
                          (compareSourceVersion && compareSourceVersion.allowedTokens) || [],
                        rightValue:
                          (compareTargetVersion && compareTargetVersion.allowedTokens) || [],
                      },
                    ]}
                  />

                  <NotificationLocalePanel
                    title="Push Locales"
                    freshness={editor.translationFreshness}
                    localeStatuses={editor.localeStatuses}
                  />

                  <NotificationSchedulePanel
                    title="Push Schedule"
                    schedule={editor.scheduledPublish}
                    busy={working}
                    onSave={handleSaveSchedule}
                    onCancel={handleCancelSchedule}
                  />

                  <NotificationDependencyGraphPanel
                    title="Push Dependencies"
                    graph={filteredDependencyGraph}
                    loading={loadingDependencies}
                    state={dependencyState}
                    stateOptions={GRAPH_STATE_OPTIONS}
                    onStateChange={(nextState) => {
                      setDependencyState(nextState);
                      loadDependencies(nextState);
                    }}
                    onRefresh={() => loadDependencies(dependencyState)}
                    onNodeClick={handleDependencyNodeClick}
                  />
                </Box>

                <Box className={classes.sectionBlock}>
                  <Typography variant="subtitle1" className={classes.sectionTitle}>
                    Revision History
                  </Typography>
                  {(editor.history || []).length === 0 ? (
                    <Typography variant="body2" color="textSecondary">
                      No revisions recorded yet.
                    </Typography>
                  ) : (
                    (editor.history || []).map((revision) => (
                      <div key={revision.notificationTemplateRevisionId} className={classes.historyItem}>
                        <div className={classes.rowInner}>
                          <div>
                            <Typography variant="subtitle2">
                              {revision.action} ({revision.state})
                            </Typography>
                            <Typography variant="body2" color="textSecondary">
                              {revision.provenance} - {formatDate(revision.audit && revision.audit.updatedDateUtc)}
                            </Typography>
                          </div>
                          <div className={classes.toolbarActions}>
                            <Button
                              variant="outlined"
                              size="small"
                              onClick={() => handleLoadRevision(revision.notificationTemplateRevisionId)}
                            >
                              View
                            </Button>
                            <Button
                              variant="outlined"
                              size="small"
                              onClick={() => handleRestoreRevision(revision.notificationTemplateRevisionId)}
                            >
                              Restore to Draft
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))
                  )}

                  {selectedRevision ? (
                    <Box mt={3}>
                      <Grid container spacing={3}>
                        <Grid item xs={12} md={6}>
                          <Paper className={classes.versionCard}>
                            <Typography variant="subtitle1" className={classes.sectionTitle}>
                              Selected Revision
                            </Typography>
                            <Typography variant="subtitle2">Title</Typography>
                            <Typography variant="body2">
                              {(selectedRevision.push && selectedRevision.push.title) || "No title."}
                            </Typography>
                            <Box mt={2}>
                              <Typography variant="subtitle2">Body</Typography>
                              <Typography variant="body2">
                                {(selectedRevision.push && selectedRevision.push.body) || "No body."}
                              </Typography>
                            </Box>
                          </Paper>
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <Paper className={classes.versionCard}>
                            <Typography variant="subtitle1" className={classes.sectionTitle}>
                              Current Draft
                            </Typography>
                            <Typography variant="subtitle2">Title</Typography>
                            <Typography variant="body2">
                              {(editor.draft && editor.draft.title) || "No title."}
                            </Typography>
                            <Box mt={2}>
                              <Typography variant="subtitle2">Body</Typography>
                              <Typography variant="body2">
                                {(editor.draft && editor.draft.body) || "No body."}
                              </Typography>
                            </Box>
                          </Paper>
                        </Grid>
                      </Grid>
                    </Box>
                  ) : null}
                </Box>
              </>
            )}
          </Paper>
        </Grid>
      </Grid>

      <TranslationDialog
        open={translationDialogOpen}
        locales={locales}
        selectedLocales={translationLocales}
        onToggleLocale={toggleTranslationLocale}
        onClose={() => {
          setTranslationDialogOpen(false);
          setTranslationLocales([]);
        }}
        onSubmit={handleQueueTranslations}
        busy={working}
      />
      <TestDestinationDialog
        open={testDestinationDialogOpen}
        override={testDestinationOverride}
        busy={working}
        onClose={() => setTestDestinationDialogOpen(false)}
        onSave={handleSaveTestDestination}
        server={testDestinationServer}
        allowEmailAddress={false}
      />
      <PushTemplateCreateDialog
        open={createDialogOpen}
        bindings={bindings}
        draft={createDraft}
        brand={activeBrand}
        onBindingChange={handleCreateBindingChange}
        onFieldChange={handleCreateFieldChange}
        onClose={() => {
          setCreateDialogOpen(false);
          setCreateDraft(createEmptyPushTemplateDraft());
        }}
        onSubmit={handleCreateTemplate}
        busy={working}
      />
    </NotificationScaffold>
  );
};

const EmailTemplatesPage = ({ match, rootMatch }) => {
  const classes = useStyles();
  const history = useHistory();
  const location = useLocation();
  const [mode, setMode] = useState("content");
  const [domainGroups, setDomainGroups] = useState([]);
  const [baseGroups, setBaseGroups] = useState([]);
  const [locales, setLocales] = useState([]);
  const [loadingList, setLoadingList] = useState(true);
  const [loadingEditor, setLoadingEditor] = useState(false);
  const [saving, setSaving] = useState(false);
  const [working, setWorking] = useState(false);
  const [search, setSearch] = useState("");
  const [lockFilter, setLockFilter] = useState("all");
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [activeBrand, setActiveBrand] = useState(BRANDS[0]);
  const [selectedLocale, setSelectedLocale] = useState(DEFAULT_LOCALE);
  const [contentEditor, setContentEditor] = useState(null);
  const [baseEditor, setBaseEditor] = useState(null);
  const [draftPreviewHtml, setDraftPreviewHtml] = useState("");
  const [publishedPreviewHtml, setPublishedPreviewHtml] = useState("");
  const [previewJson, setPreviewJson] = useState("{}");
  const [previewState, setPreviewState] = useState("Draft");
  const [previewResult, setPreviewResult] = useState(null);
  const [selectedContentRevision, setSelectedContentRevision] = useState(null);
  const [selectedBaseRevision, setSelectedBaseRevision] = useState(null);
  const [status, setStatus] = useState("");
  const [error, setError] = useState("");
  const [translationDialogOpen, setTranslationDialogOpen] = useState(false);
  const [translationLocales, setTranslationLocales] = useState([]);
  const [testDestinationDialogOpen, setTestDestinationDialogOpen] = useState(false);
  const [imagePickerOpen, setImagePickerOpen] = useState(false);
  const [imageTarget, setImageTarget] = useState("content");
  const [isBaseLayoutCollapsed, setIsBaseLayoutCollapsed] = useState(true);
  const [contentCompareSource, setContentCompareSource] = useState("draft");
  const [contentCompareTarget, setContentCompareTarget] = useState("published");
  const [baseCompareSource, setBaseCompareSource] = useState("draft");
  const [baseCompareTarget, setBaseCompareTarget] = useState("published");
  const [contentDependencyState, setContentDependencyState] = useState("Draft");
  const [baseDependencyState, setBaseDependencyState] = useState("Draft");
  const [contentDependencyGraph, setContentDependencyGraph] = useState(null);
  const [baseDependencyGraph, setBaseDependencyGraph] = useState(null);
  const [loadingContentDependencies, setLoadingContentDependencies] = useState(false);
  const [loadingBaseDependencies, setLoadingBaseDependencies] = useState(false);
  const {
    override: testDestinationOverride,
    hasOverride: hasTestDestinationOverride,
    saveOverride: saveTestDestinationOverride,
    clearOverride: clearTestDestinationOverride,
    server: testDestinationServer,
  } = useStoredTestDestinationOverride();

  const loadCatalog = async () => {
    setLoadingList(true);
    try {
      const responses = await Promise.all([
        api.get("/api/v2/ServiceCenter/notification-templates/email/domains"),
        api.get("/api/v2/ServiceCenter/notification-templates/email/base-templates"),
      ]);

      setDomainGroups((responses[0] && responses[0].data) || []);
      setBaseGroups((responses[1] && responses[1].data) || []);
    } catch (loadError) {
      setError("Unable to load email templates right now.");
    } finally {
      setLoadingList(false);
    }
  };

  const loadLocales = async () => {
    try {
      const response = await api.get(
        "/api/v2/ServiceCenter/notification-templates/locales"
      );
      setLocales(response.data || []);
    } catch (loadError) {
      setLocales([]);
    }
  };

  useEffect(() => {
    loadCatalog();
    loadLocales();
  }, []);

  useEffect(() => {
    const jump = location.state && location.state.notificationJump;
    if (!jump || !jump.templateKey || !jump.templateKind) {
      return;
    }

    if (jump.templateKind === "EmailContentTemplate") {
      setMode("content");
      loadContentEditor(
        { templateKey: jump.templateKey, locales: [jump.locale || DEFAULT_LOCALE] },
        jump.brand || activeBrand,
        jump.locale || DEFAULT_LOCALE
      );
      history.replace(location.pathname);
      return;
    }

    if (jump.templateKind === "EmailBaseTemplate") {
      setMode("base");
      loadBaseEditor(
        { templateKey: jump.templateKey, locales: [jump.locale || DEFAULT_LOCALE] },
        jump.brand || activeBrand,
        jump.locale || DEFAULT_LOCALE
      );
      history.replace(location.pathname);
    }
  }, [activeBrand, history, location.pathname, location.state]);

  const visibleGroups = useMemo(
    () => filterGroups(mode === "content" ? domainGroups : baseGroups, search, lockFilter),
    [mode, domainGroups, baseGroups, search, lockFilter]
  );

  const visibleTemplateKeys = useMemo(
    () => collectVisibleTemplateKeys(visibleGroups),
    [visibleGroups]
  );

  const loadContentEditor = async (template, brand, locale) => {
    setLoadingEditor(true);
    setError("");
    setPreviewResult(null);
    setSelectedContentRevision(null);
    setSelectedBaseRevision(null);
    setContentCompareTarget("published");
    setBaseCompareTarget("published");
    setContentDependencyGraph(null);
    setBaseDependencyGraph(null);

    try {
      const response = await api.get(
        `/api/v2/ServiceCenter/notification-templates/email/content/${template.templateKey}`,
        {
          params: {
            brand,
            locale,
          },
        }
      );

      setSelectedTemplate(template);
      setActiveBrand(brand);
      setSelectedLocale(locale);
      setContentEditor(response.data && response.data.content);
      setBaseEditor(response.data && response.data.base);
      setIsBaseLayoutCollapsed(true);
      setDraftPreviewHtml((response.data && response.data.draftPreviewHtml) || "");
      setPublishedPreviewHtml((response.data && response.data.publishedPreviewHtml) || "");

      const contentTokens =
        ((response.data &&
          response.data.content &&
          response.data.content.draft &&
          response.data.content.draft.allowedTokens) ||
          []) || [];
      const baseTokens =
        ((response.data &&
          response.data.base &&
          response.data.base.draft &&
          response.data.base.draft.allowedTokens) ||
          []) || [];
      const combinedTokens = contentTokens.concat(baseTokens).filter((value, index, values) => values.indexOf(value) === index);

      setPreviewJson(buildSampleParameterJson(combinedTokens));
    } catch (loadError) {
      setError(getApiErrorMessage(loadError, "Unable to load the email template editor."));
    } finally {
      setLoadingEditor(false);
    }
  };

  const loadBaseEditor = async (template, brand, locale) => {
    setLoadingEditor(true);
    setError("");
    setPreviewResult(null);
    setSelectedContentRevision(null);
    setSelectedBaseRevision(null);
    setBaseCompareTarget("published");
    setBaseDependencyGraph(null);

    try {
      const response = await api.get(
        `/api/v2/ServiceCenter/notification-templates/email/base/${template.templateKey}`,
        {
          params: {
            brand,
            locale,
          },
        }
      );

      setSelectedTemplate(template);
      setActiveBrand(brand);
      setSelectedLocale(locale);
      setContentEditor(null);
      setBaseEditor(response.data || null);
      setDraftPreviewHtml("");
      setPublishedPreviewHtml("");
      setPreviewJson(
        buildSampleParameterJson(
          ((response.data && response.data.draft && response.data.draft.allowedTokens) || []).concat(["subject", "email"])
        )
      );
    } catch (loadError) {
      setError(getApiErrorMessage(loadError, "Unable to load the email base template."));
    } finally {
      setLoadingEditor(false);
    }
  };

  const handleSelectTemplate = async (template) => {
    const locale = getFirstLocale(template.locales);

    if (mode === "content") {
      await loadContentEditor(template, activeBrand, locale);
      return;
    }

    await loadBaseEditor(template, activeBrand, locale);
  };

  const handleBrandChange = async (_, nextBrand) => {
    if (!selectedTemplate) {
      setActiveBrand(nextBrand);
      return;
    }

    if (mode === "content") {
      await loadContentEditor(selectedTemplate, nextBrand, selectedLocale);
      return;
    }

    await loadBaseEditor(selectedTemplate, nextBrand, selectedLocale);
  };

  const handleLocaleChange = async (event) => {
    const nextLocale = event.target.value;

    if (!selectedTemplate) {
      setSelectedLocale(nextLocale);
      return;
    }

    if (mode === "content") {
      await loadContentEditor(selectedTemplate, activeBrand, nextLocale);
      return;
    }

    await loadBaseEditor(selectedTemplate, activeBrand, nextLocale);
  };

  const updateContentDraft = (field, value) => {
    setContentEditor((current) => ({
      ...current,
      draft: {
        ...(current && current.draft ? current.draft : {}),
        [field]: value,
      },
    }));
  };

  const updateBaseDraft = (field, value) => {
    setBaseEditor((current) => ({
      ...current,
      draft: {
        ...(current && current.draft ? current.draft : {}),
        [field]: value,
      },
    }));
  };

  const handleSaveContent = async () => {
    if (!selectedTemplate || !contentEditor) {
      return;
    }

    setSaving(true);
    setStatus("");
    setError("");

    try {
      const response = await api.put(
        `/api/v2/ServiceCenter/notification-templates/email/content/${selectedTemplate.templateKey}`,
        {
          subject: contentEditor.draft && contentEditor.draft.subject,
          htmlFragment: contentEditor.draft && contentEditor.draft.htmlFragment,
          baseTemplateKey: contentEditor.draft && contentEditor.draft.baseTemplateKey,
        },
        {
          params: {
            brand: activeBrand,
            locale: selectedLocale,
          },
        }
      );

      setContentEditor(response.data && response.data.content);
      setBaseEditor(response.data && response.data.base);
      setDraftPreviewHtml((response.data && response.data.draftPreviewHtml) || "");
      setPublishedPreviewHtml((response.data && response.data.publishedPreviewHtml) || "");
      setStatus("Email content draft saved.");
      await loadCatalog();
    } catch (saveError) {
      setError("Unable to save the email content draft.");
    } finally {
      setSaving(false);
    }
  };

  const handlePublishContent = async () => {
    if (!selectedTemplate) {
      return;
    }

    setWorking(true);
    setStatus("");
    setError("");

    try {
      await api.post(
        `/api/v2/ServiceCenter/notification-templates/email/content/${selectedTemplate.templateKey}/publish`,
        null,
        {
          params: {
            brand: activeBrand,
            locale: selectedLocale,
          },
        }
      );

      setStatus("Email content draft published.");
      await loadCatalog();
      await loadContentEditor(selectedTemplate, activeBrand, selectedLocale);
    } catch (publishError) {
      setError("Unable to publish the email content draft.");
    } finally {
      setWorking(false);
    }
  };

  const handleSaveBase = async () => {
    if (!baseEditor) {
      return;
    }

    setSaving(true);
    setStatus("");
    setError("");

    try {
      const response = await api.put(
        `/api/v2/ServiceCenter/notification-templates/email/base/${baseEditor.templateKey}`,
        {
          htmlLayout: baseEditor.draft && baseEditor.draft.htmlLayout,
        },
        {
          params: {
            brand: baseEditor.brand || activeBrand,
            locale: baseEditor.locale || selectedLocale,
          },
        }
      );

      if (mode === "content") {
        setContentEditor(response.data && response.data.content);
        setBaseEditor(response.data && response.data.base);
        setDraftPreviewHtml((response.data && response.data.draftPreviewHtml) || "");
        setPublishedPreviewHtml((response.data && response.data.publishedPreviewHtml) || "");
      } else {
        setBaseEditor(response.data || null);
      }

      setStatus("Email base draft saved.");
      await loadCatalog();
    } catch (saveError) {
      setError("Unable to save the email base draft.");
    } finally {
      setSaving(false);
    }
  };

  const handlePublishBase = async () => {
    if (!baseEditor) {
      return;
    }

    setWorking(true);
    setStatus("");
    setError("");

    try {
      await api.post(
        `/api/v2/ServiceCenter/notification-templates/email/base/${baseEditor.templateKey}/publish`,
        null,
        {
          params: {
            brand: baseEditor.brand || activeBrand,
            locale: baseEditor.locale || selectedLocale,
          },
        }
      );

      setStatus("Email base draft published.");
      await loadCatalog();

      if (mode === "content" && selectedTemplate) {
        await loadContentEditor(selectedTemplate, activeBrand, selectedLocale);
      } else if (selectedTemplate) {
        await loadBaseEditor(selectedTemplate, activeBrand, selectedLocale);
      }
    } catch (publishError) {
      setError("Unable to publish the email base draft.");
    } finally {
      setWorking(false);
    }
  };

  const handlePreview = async () => {
    if (!selectedTemplate) {
      return;
    }

    setWorking(true);
    setStatus("");
    setError("");

    try {
      const response = await api.post(
        `/api/v2/ServiceCenter/notification-templates/email/${mode === "content" ? "content" : "base"}/${selectedTemplate.templateKey}/preview`,
        {
          parameters: parseParameterJson(previewJson),
          state: previewState,
        },
        {
          params: {
            brand: activeBrand,
            locale: selectedLocale,
          },
        }
      );

      setPreviewResult(response.data);
      setStatus(`Preview rendered from ${previewState.toLowerCase()} state.`);
    } catch (previewError) {
      setError("Unable to render the email preview. Check the sample token JSON.");
    } finally {
      setWorking(false);
    }
  };

  const handleSaveTestDestination = (nextOverride) => {
    const normalized = saveTestDestinationOverride(nextOverride);
    setStatus(
      normalized
        ? "Test destination overrides saved locally."
        : "Test destination overrides cleared."
    );
    setError("");
    setTestDestinationDialogOpen(false);
  };

  const handleClearTestDestination = () => {
    clearTestDestinationOverride();
    setStatus("Test destination overrides cleared.");
    setError("");
  };

  const handleSendTestEmail = async () => {
    if (!selectedTemplate) {
      return;
    }

    setWorking(true);
    setStatus("");
    setError("");

    try {
      const response = await api.post(
        `/api/v2/ServiceCenter/notification-templates/email/${
          mode === "content" ? "content" : "base"
        }/${selectedTemplate.templateKey}/test-send`,
        buildTestSendRequestBody(
          previewJson,
          previewState,
          hasTestDestinationOverride ? testDestinationOverride : null
        ),
        {
          params: {
            brand: activeBrand,
            locale: selectedLocale,
          },
        }
      );

      setStatus(`Test email sent to ${describeResolvedTestDestination(response.data)}.`);
    } catch (sendError) {
      setError(
        getApiErrorMessage(
          sendError,
          "Unable to send the email test notification. Check the sample token JSON."
        )
      );
    } finally {
      setWorking(false);
    }
  };

  const handleToggleContentLock = async (isLocked) => {
    if (!selectedTemplate || !contentEditor) {
      return;
    }

    setWorking(true);
    setStatus("");
    setError("");

    try {
      await api.post(
        `/api/v2/ServiceCenter/notification-templates/email/content/${selectedTemplate.templateKey}/${isLocked ? "unlock" : "lock"}`,
        null,
        {
          params: {
            brand: activeBrand,
            locale: selectedLocale,
          },
        }
      );

      setStatus(isLocked ? "Content language unlocked." : "Content language locked.");
      await loadCatalog();
      await loadContentEditor(selectedTemplate, activeBrand, selectedLocale);
    } catch (lockError) {
      setError("Unable to update the content lock state.");
    } finally {
      setWorking(false);
    }
  };

  const handleToggleBaseLock = async (isLocked) => {
    if (!baseEditor) {
      return;
    }

    setWorking(true);
    setStatus("");
    setError("");

    try {
      await api.post(
        `/api/v2/ServiceCenter/notification-templates/email/base/${baseEditor.templateKey}/${isLocked ? "unlock" : "lock"}`,
        null,
        {
          params: {
            brand: baseEditor.brand || activeBrand,
            locale: baseEditor.locale || selectedLocale,
          },
        }
      );

      setStatus(isLocked ? "Base language unlocked." : "Base language locked.");
      await loadCatalog();

      if (mode === "content" && selectedTemplate) {
        await loadContentEditor(selectedTemplate, activeBrand, selectedLocale);
      } else if (selectedTemplate) {
        await loadBaseEditor(selectedTemplate, activeBrand, selectedLocale);
      }
    } catch (lockError) {
      setError("Unable to update the base lock state.");
    } finally {
      setWorking(false);
    }
  };

  const handleUnlockAll = async () => {
    if (visibleTemplateKeys.length === 0) {
      return;
    }

    setWorking(true);
    setStatus("");
    setError("");

    try {
      const response = await api.post(
        "/api/v2/ServiceCenter/notification-templates/email/unlock-all",
        {
          templateKeys: visibleTemplateKeys,
          templateKind: mode === "content" ? "EmailContentTemplate" : "EmailBaseTemplate",
        }
      );

      setStatus(`Unlocked ${response.data.unlockedCount || 0} email language records.`);
      await loadCatalog();

      if (selectedTemplate) {
        if (mode === "content") {
          await loadContentEditor(selectedTemplate, activeBrand, selectedLocale);
        } else {
          await loadBaseEditor(selectedTemplate, activeBrand, selectedLocale);
        }
      }
    } catch (unlockError) {
      setError("Unable to unlock the filtered email templates.");
    } finally {
      setWorking(false);
    }
  };

  const toggleTranslationLocale = (locale) => {
    setTranslationLocales((current) =>
      current.indexOf(locale) >= 0
        ? current.filter((item) => item !== locale)
        : current.concat(locale)
    );
  };

  const handleQueueTranslations = async () => {
    if (visibleTemplateKeys.length === 0 || translationLocales.length === 0) {
      return;
    }

    setWorking(true);
    setStatus("");
    setError("");

    try {
      const response = await api.post(
        "/api/v2/ServiceCenter/notification-templates/translations/jobs",
        {
          channel: "email",
          templateKind: mode === "content" ? "EmailContentTemplate" : "EmailBaseTemplate",
          templateKeys: visibleTemplateKeys,
          targetLocales: translationLocales,
          brands: BRANDS,
        }
      );

      setStatus(
        `Queued email translations for ${response.data.templateCount || 0} templates.`
      );
      setTranslationDialogOpen(false);
      setTranslationLocales([]);
    } catch (jobError) {
      setError("Unable to queue the translation job.");
    } finally {
      setWorking(false);
    }
  };

  const handleLoadContentRevision = async (revisionId) => {
    if (!selectedTemplate) {
      return;
    }

    setWorking(true);
    setError("");

    try {
      const response = await api.get(
        `/api/v2/ServiceCenter/notification-templates/email/content/${selectedTemplate.templateKey}/history/${revisionId}`,
        {
          params: {
            brand: activeBrand,
            locale: selectedLocale,
          },
        }
      );

      setSelectedContentRevision(response.data);
    } catch (loadError) {
      setError("Unable to load that content revision.");
    } finally {
      setWorking(false);
    }
  };

  const handleLoadBaseRevision = async (revisionId) => {
    if (!selectedTemplate && !baseEditor) {
      return;
    }

    const templateKey = (baseEditor && baseEditor.templateKey) || (selectedTemplate && selectedTemplate.templateKey);

    setWorking(true);
    setError("");

    try {
      const response = await api.get(
        `/api/v2/ServiceCenter/notification-templates/email/base/${templateKey}/history/${revisionId}`,
        {
          params: {
            brand: (baseEditor && baseEditor.brand) || activeBrand,
            locale: (baseEditor && baseEditor.locale) || selectedLocale,
          },
        }
      );

      setSelectedBaseRevision(response.data);
    } catch (loadError) {
      setError("Unable to load that base revision.");
    } finally {
      setWorking(false);
    }
  };

  const handleRestoreContentRevision = async (revisionId) => {
    if (!selectedTemplate) {
      return;
    }

    setWorking(true);
    setStatus("");
    setError("");

    try {
      const response = await api.post(
        `/api/v2/ServiceCenter/notification-templates/email/content/${selectedTemplate.templateKey}/history/${revisionId}/restore`,
        null,
        {
          params: {
            brand: activeBrand,
            locale: selectedLocale,
          },
        }
      );

      setContentEditor(response.data && response.data.content);
      setBaseEditor(response.data && response.data.base);
      setDraftPreviewHtml((response.data && response.data.draftPreviewHtml) || "");
      setPublishedPreviewHtml((response.data && response.data.publishedPreviewHtml) || "");
      setStatus("Content revision restored into draft.");
      setSelectedContentRevision(null);
      await loadCatalog();
    } catch (restoreError) {
      setError("Unable to restore that content revision.");
    } finally {
      setWorking(false);
    }
  };

  const handleRestoreBaseRevision = async (revisionId) => {
    const templateKey = (baseEditor && baseEditor.templateKey) || (selectedTemplate && selectedTemplate.templateKey);
    if (!templateKey) {
      return;
    }

    setWorking(true);
    setStatus("");
    setError("");

    try {
      const response = await api.post(
        `/api/v2/ServiceCenter/notification-templates/email/base/${templateKey}/history/${revisionId}/restore`,
        null,
        {
          params: {
            brand: (baseEditor && baseEditor.brand) || activeBrand,
            locale: (baseEditor && baseEditor.locale) || selectedLocale,
          },
        }
      );

      if (mode === "content") {
        setContentEditor(response.data && response.data.content);
        setBaseEditor(response.data && response.data.base);
        setDraftPreviewHtml((response.data && response.data.draftPreviewHtml) || "");
        setPublishedPreviewHtml((response.data && response.data.publishedPreviewHtml) || "");
      } else {
        setBaseEditor(response.data || null);
      }

      setStatus("Base revision restored into draft.");
      setSelectedBaseRevision(null);
      await loadCatalog();
    } catch (restoreError) {
      setError("Unable to restore that base revision.");
    } finally {
      setWorking(false);
    }
  };

  const openImageLibrary = (targetField) => {
    setImageTarget(targetField);
    setImagePickerOpen(true);
  };

  const handleInsertImageReference = (value) => {
    if (imageTarget === "content") {
      updateContentDraft(
        "htmlFragment",
        insertReference(contentEditor && contentEditor.draft && contentEditor.draft.htmlFragment, value)
      );
    } else {
      updateBaseDraft(
        "htmlLayout",
        insertReference(baseEditor && baseEditor.draft && baseEditor.draft.htmlLayout, value)
      );
    }
  };

  const handleContentCompareTargetChange = async (nextTarget) => {
    setContentCompareTarget(nextTarget);

    if (nextTarget.indexOf("revision:") !== 0) {
      return;
    }

    await handleLoadContentRevision(nextTarget.replace("revision:", ""));
  };

  const handleBaseCompareTargetChange = async (nextTarget) => {
    setBaseCompareTarget(nextTarget);

    if (nextTarget.indexOf("revision:") !== 0) {
      return;
    }

    await handleLoadBaseRevision(nextTarget.replace("revision:", ""));
  };

  const handleSaveContentSchedule = async (runAtValue) => {
    if (!selectedTemplate || !contentEditor) {
      return;
    }

    const runAtUtc = parseDateTimeInput(runAtValue);
    if (!runAtUtc) {
      setError("Choose a valid content schedule time.");
      return;
    }

    setWorking(true);
    setStatus("");
    setError("");

    try {
      const response = await api.put(
        `/api/v2/ServiceCenter/notification-templates/email/content/${selectedTemplate.templateKey}/schedule`,
        {
          runAtUtc,
        },
        {
          params: {
            brand: activeBrand,
            locale: selectedLocale,
          },
        }
      );

      setContentEditor((current) => ({
        ...current,
        scheduledPublish: response.data,
      }));
      setStatus("Email content scheduled for publish.");
    } catch (scheduleError) {
      setError(
        getApiErrorMessage(scheduleError, "Unable to schedule the email content draft.")
      );
    } finally {
      setWorking(false);
    }
  };

  const handleCancelContentSchedule = async () => {
    if (!selectedTemplate || !contentEditor) {
      return;
    }

    setWorking(true);
    setStatus("");
    setError("");

    try {
      await api.delete(
        `/api/v2/ServiceCenter/notification-templates/email/content/${selectedTemplate.templateKey}/schedule`,
        {
          params: {
            brand: activeBrand,
            locale: selectedLocale,
          },
        }
      );

      setContentEditor((current) => ({
        ...current,
        scheduledPublish: null,
      }));
      setStatus("Email content schedule canceled.");
    } catch (cancelError) {
      setError(
        getApiErrorMessage(cancelError, "Unable to cancel the email content schedule.")
      );
    } finally {
      setWorking(false);
    }
  };

  const handleSaveBaseSchedule = async (runAtValue) => {
    if (!baseEditor) {
      return;
    }

    const runAtUtc = parseDateTimeInput(runAtValue);
    if (!runAtUtc) {
      setError("Choose a valid base schedule time.");
      return;
    }

    setWorking(true);
    setStatus("");
    setError("");

    try {
      const response = await api.put(
        `/api/v2/ServiceCenter/notification-templates/email/base/${baseEditor.templateKey}/schedule`,
        {
          runAtUtc,
        },
        {
          params: {
            brand: baseEditor.brand || activeBrand,
            locale: baseEditor.locale || selectedLocale,
          },
        }
      );

      setBaseEditor((current) => ({
        ...current,
        scheduledPublish: response.data,
      }));
      setStatus("Email base scheduled for publish.");
    } catch (scheduleError) {
      setError(
        getApiErrorMessage(scheduleError, "Unable to schedule the email base draft.")
      );
    } finally {
      setWorking(false);
    }
  };

  const handleCancelBaseSchedule = async () => {
    if (!baseEditor) {
      return;
    }

    setWorking(true);
    setStatus("");
    setError("");

    try {
      await api.delete(
        `/api/v2/ServiceCenter/notification-templates/email/base/${baseEditor.templateKey}/schedule`,
        {
          params: {
            brand: baseEditor.brand || activeBrand,
            locale: baseEditor.locale || selectedLocale,
          },
        }
      );

      setBaseEditor((current) => ({
        ...current,
        scheduledPublish: null,
      }));
      setStatus("Email base schedule canceled.");
    } catch (cancelError) {
      setError(
        getApiErrorMessage(cancelError, "Unable to cancel the email base schedule.")
      );
    } finally {
      setWorking(false);
    }
  };

  const loadContentDependencies = async (nextState = contentDependencyState) => {
    if (!selectedTemplate) {
      return;
    }

    setLoadingContentDependencies(true);
    setError("");

    try {
      const response = await api.get(
        `/api/v2/ServiceCenter/notification-templates/email/content/${selectedTemplate.templateKey}/dependencies`,
        {
          params: {
            brand: activeBrand,
            locale: selectedLocale,
            state: nextState,
          },
        }
      );

      setContentDependencyGraph(response.data || null);
    } catch (dependencyError) {
      setError(
        getApiErrorMessage(dependencyError, "Unable to load email content dependencies.")
      );
    } finally {
      setLoadingContentDependencies(false);
    }
  };

  const loadBaseDependencies = async (
    nextState = baseDependencyState,
    nextTemplate = baseEditor
  ) => {
    if (!nextTemplate) {
      return;
    }

    setLoadingBaseDependencies(true);
    setError("");

    try {
      const response = await api.get(
        `/api/v2/ServiceCenter/notification-templates/email/base/${nextTemplate.templateKey}/dependencies`,
        {
          params: {
            brand: nextTemplate.brand || activeBrand,
            locale: nextTemplate.locale || selectedLocale,
            state: nextState,
          },
        }
      );

      setBaseDependencyGraph(response.data || null);
    } catch (dependencyError) {
      setError(getApiErrorMessage(dependencyError, "Unable to load email base dependencies."));
    } finally {
      setLoadingBaseDependencies(false);
    }
  };

  const handleEmailDependencyNodeClick = async (node) => {
    if (!node) {
      return;
    }

    if (node.notificationImageAssetId) {
      history.push(`${rootMatch.url}/images`, {
        notificationJump: {
          type: "image",
          assetId: node.notificationImageAssetId,
        },
      });
      return;
    }

    if (node.templateKind === "PushTemplate" && node.templateKey) {
      history.push(`${rootMatch.url}/push`, {
        notificationJump: {
          templateKind: node.templateKind,
          templateKey: node.templateKey,
          brand: node.brand,
          locale: node.locale,
        },
      });
      return;
    }

    if (node.templateKind === "EmailContentTemplate" && node.templateKey) {
      setMode("content");
      await loadContentEditor(
        { templateKey: node.templateKey, locales: [node.locale || DEFAULT_LOCALE] },
        node.brand || activeBrand,
        node.locale || selectedLocale
      );
      return;
    }

    if (node.templateKind === "EmailBaseTemplate" && node.templateKey) {
      setMode("base");
      await loadBaseEditor(
        { templateKey: node.templateKey, locales: [node.locale || DEFAULT_LOCALE] },
        node.brand || activeBrand,
        node.locale || selectedLocale
      );
    }
  };

  const combinedLintIssues =
    (previewResult && previewResult.lintIssues) ||
    (mode === "content"
      ? ((contentEditor && contentEditor.lintIssues) || []).concat((baseEditor && baseEditor.lintIssues) || [])
      : ((baseEditor && baseEditor.lintIssues) || []));

  const previewHtml =
    (previewResult && previewResult.emailHtml) ||
    (previewState === "Published" ? publishedPreviewHtml : draftPreviewHtml);
  const contentCompareOptions = createHistoryCompareOptions(
    contentEditor && contentEditor.history
  );
  const baseCompareOptions = createHistoryCompareOptions(baseEditor && baseEditor.history);
  const contentCompareTargetLabel = getCompareTargetLabel(
    contentCompareTarget,
    contentCompareOptions
  );
  const baseCompareTargetLabel = getCompareTargetLabel(
    baseCompareTarget,
    baseCompareOptions
  );
  const contentCompareSourceVersion =
    contentCompareSource === "published"
      ? contentEditor && contentEditor.published
      : contentEditor && contentEditor.draft;
  const contentCompareTargetVersion =
    contentCompareTarget.indexOf("revision:") === 0
      ? selectedContentRevision && selectedContentRevision.emailContent
      : contentEditor && contentEditor.published;
  const baseCompareSourceVersion =
    baseCompareSource === "published"
      ? baseEditor && baseEditor.published
      : baseEditor && baseEditor.draft;
  const baseCompareTargetVersion =
    baseCompareTarget.indexOf("revision:") === 0
      ? selectedBaseRevision && selectedBaseRevision.emailBase
      : baseEditor && baseEditor.published;

  return (
    <NotificationScaffold
      match={match}
      rootMatch={rootMatch}
      heading="Email Template Editor"
      subtitle="Email content and base layouts now have separate drafts, shared preview linting, and a reusable image library so changes can be staged safely."
    >
      <StatusBanners status={status} error={error} />

      <TestDestinationToolbar
        override={testDestinationOverride}
        busy={working}
        onOpen={() => setTestDestinationDialogOpen(true)}
        onClear={handleClearTestDestination}
        buttonTestId="email-test-destination-button"
        clearButtonTestId="email-clear-test-destination-button"
      >
        <Button
          variant="outlined"
          color="primary"
          onClick={() => setTranslationDialogOpen(true)}
          disabled={visibleTemplateKeys.length === 0 || working}
        >
          Add Languages
        </Button>
        <Button
          variant="outlined"
          onClick={handleUnlockAll}
          disabled={visibleTemplateKeys.length === 0 || working}
        >
          Unlock All
        </Button>
      </TestDestinationToolbar>

      <Tabs
        value={mode}
        onChange={(_, nextMode) => {
          setMode(nextMode);
          setSelectedTemplate(null);
          setContentEditor(null);
          setBaseEditor(null);
          setDraftPreviewHtml("");
          setPublishedPreviewHtml("");
          setPreviewResult(null);
          setStatus("");
          setError("");
          setContentDependencyGraph(null);
          setBaseDependencyGraph(null);
          setContentCompareTarget("published");
          setBaseCompareTarget("published");
        }}
        indicatorColor="primary"
        textColor="primary"
      >
        <Tab value="content" label="Email Templates" />
        <Tab value="base" label="Base Templates" />
      </Tabs>

      <Grid container spacing={3}>
        <Grid item xs={12} md={4}>
          <Paper className={classes.listPanel}>
            <TextField
              fullWidth
              variant="outlined"
              label={mode === "content" ? "Search email templates" : "Search base templates"}
              placeholder={mode === "content" ? "ConfirmAccountEmail, trading..." : "Layout, InternalBaseLayout..."}
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              className={classes.searchField}
            />

            <div className={classes.filterRow}>
              {LOCK_FILTERS.map((option) => (
                <Chip
                  key={option.value}
                  clickable
                  color={lockFilter === option.value ? "primary" : "default"}
                  label={option.label}
                  onClick={() => setLockFilter(option.value)}
                />
              ))}
            </div>

            {loadingList ? (
              <div className={classes.loadingWrap}>
                <CircularProgress />
              </div>
            ) : visibleGroups.length === 0 ? (
              <div className={classes.emptyState}>
                <Typography variant="subtitle1">No templates found</Typography>
                <Typography variant="body2">
                  Try a different search or filter combination.
                </Typography>
              </div>
            ) : (
              visibleGroups.map((group) => (
                <Paper
                  key={group.domain || group.groupName}
                  className={classes.groupCard}
                >
                  <div className={classes.groupHeader}>{group.domain || group.groupName}</div>
                  <List className={classes.list}>
                    {(group.templates || []).map((template, index) => (
                      <React.Fragment key={template.templateKey}>
                        <ListItem
                          button
                          alignItems="flex-start"
                          className={classes.row}
                          onClick={() => handleSelectTemplate(template)}
                          data-testid={`email-template-row-${template.templateKey}`}
                        >
                          <div className={classes.rowInner}>
                            <div>
                              <Typography variant="subtitle1" className={classes.rowTitle}>
                                {template.displayName}
                              </Typography>
                              <Typography className={classes.rowKey}>
                                {template.templateKey}
                              </Typography>
                              <Typography variant="body2" className={classes.rowDescription}>
                                {template.description}
                              </Typography>
                              <div className={classes.chipRow}>
                                {"baseTemplateKey" in template ? (
                                  <Chip size="small" label={`Base: ${template.baseTemplateKey}`} />
                                ) : null}
                                {template.staleLocaleCount ? (
                                  <Chip size="small" color="secondary" label={`Stale locales: ${template.staleLocaleCount}`} />
                                ) : null}
                                {template.missingLocaleCount ? (
                                  <Chip size="small" label={`Missing locales: ${template.missingLocaleCount}`} />
                                ) : null}
                                {template.hasDraftChanges ? (
                                  <Chip size="small" color="primary" label="Draft changes" />
                                ) : null}
                                <Chip size="small" label={`Locales: ${(template.locales || []).join(", ")}`} />
                                <Chip size="small" label={`Brands: ${(template.brands || []).join(", ")}`} />
                              </div>
                            </div>
                            <Typography variant="body2" className={classes.rowMeta}>
                              {formatDate(template.lastPublishedUtc || template.lastUpdatedUtc)}
                            </Typography>
                          </div>
                        </ListItem>
                        {index < group.templates.length - 1 ? <Divider /> : null}
                      </React.Fragment>
                    ))}
                  </List>
                </Paper>
              ))
            )}
          </Paper>
        </Grid>

        <Grid item xs={12} md={8}>
          <Paper className={classes.editorPanel}>
            {loadingEditor ? (
              <div className={classes.loadingWrap}>
                <CircularProgress />
              </div>
            ) : !selectedTemplate ? (
              <div className={classes.emptyState}>
                <Typography variant="h6">Select an email template</Typography>
                <Typography variant="body2">
                  Choose a content template or switch to the base-template mode
                  to edit shared wrappers directly.
                </Typography>
              </div>
            ) : (
              <>
                <Tabs
                  value={activeBrand}
                  onChange={handleBrandChange}
                  indicatorColor="primary"
                  textColor="primary"
                >
                  {BRANDS.map((brand) => (
                    <Tab key={brand} value={brand} label={brand} />
                  ))}
                </Tabs>

                <div className={classes.selectRow}>
                  <FormControl variant="outlined" className={classes.selectField}>
                    <InputLabel id="email-language-select-label">Language</InputLabel>
                    <Select
                      labelId="email-language-select-label"
                      value={selectedLocale}
                      onChange={handleLocaleChange}
                      label="Language"
                    >
                      {(((mode === "content"
                        ? contentEditor && contentEditor.availableLocales
                        : baseEditor && baseEditor.availableLocales) || [])).map((locale) => {
                        const localeOption = (locales || []).find((item) => item.locale === locale);

                        return (
                          <MenuItem key={locale} value={locale}>
                            {localeOption ? localeOption.displayName : locale}
                          </MenuItem>
                        );
                      })}
                    </Select>
                  </FormControl>
                </div>

                {mode === "content" && contentEditor ? (
                  <>
                    <div className={classes.editorMeta}>
                      <Chip label={`Domain: ${contentEditor.domain}`} />
                      <Chip label={`Provenance: ${contentEditor.provenance && contentEditor.provenance.value}`} />
                      {contentEditor.hasDraftChanges ? <Chip color="primary" label="Content draft" /> : null}
                      {baseEditor && baseEditor.hasDraftChanges ? <Chip color="primary" label="Base draft" /> : null}
                    </div>

                    <Grid container spacing={2}>
                      <Grid item xs={12} md={6}>
                        <Paper className={classes.versionCard}>
                          <Typography variant="subtitle1" className={classes.sectionTitle}>
                            Draft Content
                          </Typography>
                          <TextField
                            id="email-template-subject"
                            fullWidth
                            variant="outlined"
                            label="Email Subject"
                            value={(contentEditor.draft && contentEditor.draft.subject) || ""}
                            onChange={(event) => updateContentDraft("subject", event.target.value)}
                            className={classes.field}
                          />
                          <TextField
                            id="email-template-base-key"
                            fullWidth
                            variant="outlined"
                            label="Base Template Key"
                            value={(contentEditor.draft && contentEditor.draft.baseTemplateKey) || ""}
                            onChange={(event) => updateContentDraft("baseTemplateKey", event.target.value)}
                            className={classes.field}
                          />
                          <TextField
                            id="email-template-html-fragment"
                            fullWidth
                            multiline
                            minRows={10}
                            variant="outlined"
                            label="Email HTML Fragment"
                            value={(contentEditor.draft && contentEditor.draft.htmlFragment) || ""}
                            onChange={(event) => updateContentDraft("htmlFragment", event.target.value)}
                            className={classes.field}
                          />
                          <div className={classes.actionRow}>
                            <Button
                              variant="outlined"
                              color="primary"
                              onClick={() => openImageLibrary("content")}
                              data-testid="email-open-image-library-button"
                            >
                              Image Library
                            </Button>
                            <Button
                              color="primary"
                              variant="contained"
                              onClick={handleSaveContent}
                              disabled={saving || working}
                              data-testid="email-content-save-button"
                            >
                              {saving ? "Saving..." : "Save Content Draft"}
                            </Button>
                            <Button
                              variant="contained"
                              color="secondary"
                              onClick={handlePublishContent}
                              disabled={saving || working}
                            >
                              Publish Content
                            </Button>
                            <Button
                              variant="outlined"
                              onClick={() => handleToggleContentLock(contentEditor.isLocked)}
                              disabled={saving || working}
                            >
                              {contentEditor.isLocked ? "Unlock Content" : "Lock Content"}
                            </Button>
                          </div>
                        </Paper>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <Paper className={classes.versionCard}>
                          <Typography variant="subtitle1" className={classes.sectionTitle}>
                            Published Content
                          </Typography>
                          <Typography variant="subtitle2">Subject</Typography>
                          <Typography variant="body2">
                            {(contentEditor.published && contentEditor.published.subject) || "No published subject."}
                          </Typography>
                          <Box mt={2}>
                            <Typography variant="subtitle2">HTML Fragment</Typography>
                            <div className={classes.codeBlock}>
                              {(contentEditor.published && contentEditor.published.htmlFragment) || "No published HTML."}
                            </div>
                          </Box>
                        </Paper>
                      </Grid>
                    </Grid>

                    {baseEditor ? (
                      <Box mt={3}>
                        <div className={classes.collapsibleSectionHeader}>
                          <div className={classes.collapsibleSectionMeta}>
                            <Typography variant="subtitle1" className={classes.collapsibleSectionTitle}>
                              Base Layout
                            </Typography>
                            <Typography variant="body2" className={classes.collapsibleSectionDescription}>
                              {`${baseEditor.displayName || "Shared layout"} (${baseEditor.templateKey})`}
                            </Typography>
                          </div>
                          <Button
                            variant="outlined"
                            size="small"
                            onClick={() => setIsBaseLayoutCollapsed((current) => !current)}
                            data-testid="email-base-layout-toggle"
                          >
                            {isBaseLayoutCollapsed ? "Expand Base Layout" : "Collapse Base Layout"}
                          </Button>
                        </div>
                        <Collapse in={!isBaseLayoutCollapsed} timeout="auto" unmountOnExit>
                          <Grid container spacing={2}>
                            <Grid item xs={12} md={6}>
                              <Paper className={classes.versionCard}>
                                <Typography variant="subtitle1" className={classes.sectionTitle}>
                                  Draft Base Layout
                                </Typography>
                                <TextField
                                  id="email-base-html-layout"
                                  fullWidth
                                  multiline
                                  minRows={10}
                                  variant="outlined"
                                  label="Base HTML Layout"
                                  value={(baseEditor.draft && baseEditor.draft.htmlLayout) || ""}
                                  onChange={(event) => updateBaseDraft("htmlLayout", event.target.value)}
                                  className={classes.field}
                                />
                                <div className={classes.actionRow}>
                                  <Button
                                    variant="outlined"
                                    color="primary"
                                    onClick={() => openImageLibrary("base")}
                                  >
                                    Image Library
                                  </Button>
                                  <Button
                                    variant="outlined"
                                    color="primary"
                                    onClick={handleSaveBase}
                                    disabled={saving || working}
                                    data-testid="email-base-save-button"
                                  >
                                    {saving ? "Saving..." : "Save Base Draft"}
                                  </Button>
                                  <Button
                                    variant="contained"
                                    color="secondary"
                                    onClick={handlePublishBase}
                                    disabled={saving || working}
                                  >
                                    Publish Base
                                  </Button>
                                  <Button
                                    variant="outlined"
                                    onClick={() => handleToggleBaseLock(baseEditor.isLocked)}
                                    disabled={saving || working}
                                  >
                                    {baseEditor.isLocked ? "Unlock Base" : "Lock Base"}
                                  </Button>
                                </div>
                              </Paper>
                            </Grid>
                            <Grid item xs={12} md={6}>
                              <Paper className={classes.versionCard}>
                                <Typography variant="subtitle1" className={classes.sectionTitle}>
                                  Published Base Layout
                                </Typography>
                                <div className={classes.codeBlock}>
                                  {(baseEditor.published && baseEditor.published.htmlLayout) || "No published base HTML."}
                                </div>
                              </Paper>
                            </Grid>
                          </Grid>
                        </Collapse>
                      </Box>
                    ) : null}

                    <Box mt={4}>
                      <Typography variant="subtitle1" className={classes.sectionTitle}>
                        Preview Sandbox
                      </Typography>
                      <Grid container spacing={2}>
                        <Grid item xs={12} md={4}>
                          <FormControl variant="outlined" className={classes.selectField}>
                            <InputLabel id="email-preview-state-label">Preview State</InputLabel>
                            <Select
                              labelId="email-preview-state-label"
                              value={previewState}
                              onChange={(event) => setPreviewState(event.target.value)}
                              label="Preview State"
                            >
                              {PREVIEW_STATES.map((option) => (
                                <MenuItem key={option.value} value={option.value}>
                                  {option.label}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </Grid>
                        <Grid item xs={12}>
                          <TextField
                            id="email-preview-json"
                            fullWidth
                            multiline
                            minRows={6}
                            variant="outlined"
                            label="Sample token JSON"
                            value={previewJson}
                            onChange={(event) => setPreviewJson(event.target.value)}
                          />
                        </Grid>
                      </Grid>
                      <div className={classes.actionRow}>
                        <Button
                          variant="outlined"
                          color="primary"
                          onClick={handlePreview}
                          disabled={working}
                          data-testid="email-preview-button"
                        >
                          Render Preview
                        </Button>
                        <Button
                          variant="contained"
                          color="primary"
                          onClick={handleSendTestEmail}
                          disabled={working}
                          data-testid="email-test-send-button"
                        >
                          Send Test Email
                        </Button>
                      </div>

                      <Grid container spacing={2}>
                        <Grid item xs={12} md={7}>
                          <Paper className={classes.versionCard}>
                            <Typography variant="subtitle1" className={classes.sectionTitle}>
                              Preview
                            </Typography>
                            <Typography variant="subtitle2">
                              {(previewResult && previewResult.emailSubject) || "Preview Subject"}
                            </Typography>
                            <iframe
                              title="Email preview"
                              srcDoc={previewHtml || "<p>No preview available.</p>"}
                              className={classes.previewFrame}
                            />
                          </Paper>
                        </Grid>
                        <Grid item xs={12} md={5}>
                          <Paper className={classes.versionCard}>
                            <Typography variant="subtitle1" className={classes.sectionTitle}>
                              Lint Issues
                            </Typography>
                            <LintIssuesPanel
                              issues={combinedLintIssues}
                              emptyLabel="No issues detected for this template."
                            />
                          </Paper>
                        </Grid>
                      </Grid>
                    </Box>

                    <Box mt={4}>
                      <NotificationChangesPanel
                        title="Email Content Changes"
                        compareSource={contentCompareSource}
                        compareTarget={contentCompareTarget}
                        compareTargetOptions={contentCompareOptions}
                        onCompareSourceChange={setContentCompareSource}
                        onCompareTargetChange={handleContentCompareTargetChange}
                        leftLabel={contentCompareSource === "published" ? "Published" : "Draft"}
                        rightLabel={contentCompareTargetLabel}
                        fields={[
                          {
                            label: "Subject",
                            leftValue:
                              contentCompareSourceVersion &&
                              contentCompareSourceVersion.subject,
                            rightValue:
                              contentCompareTargetVersion &&
                              contentCompareTargetVersion.subject,
                          },
                          {
                            label: "HTML Fragment",
                            leftValue:
                              contentCompareSourceVersion &&
                              contentCompareSourceVersion.htmlFragment,
                            rightValue:
                              contentCompareTargetVersion &&
                              contentCompareTargetVersion.htmlFragment,
                          },
                        ]}
                        metadata={[
                          {
                            label: "Base Template Key",
                            leftValue:
                              contentCompareSourceVersion &&
                              contentCompareSourceVersion.baseTemplateKey,
                            rightValue:
                              contentCompareTargetVersion &&
                              contentCompareTargetVersion.baseTemplateKey,
                          },
                          {
                            label: "Tokens",
                            leftValue:
                              (contentCompareSourceVersion &&
                                contentCompareSourceVersion.allowedTokens) ||
                              [],
                            rightValue:
                              (contentCompareTargetVersion &&
                                contentCompareTargetVersion.allowedTokens) ||
                              [],
                          },
                          {
                            label: "Images",
                            leftValue: formatAssetReferenceNames(
                              contentCompareSourceVersion &&
                                contentCompareSourceVersion.referencedImages
                            ),
                            rightValue: formatAssetReferenceNames(
                              contentCompareTargetVersion &&
                                contentCompareTargetVersion.referencedImages
                            ),
                          },
                        ]}
                      />

                      {baseEditor ? (
                        <NotificationChangesPanel
                          title="Email Base Changes"
                          compareSource={baseCompareSource}
                          compareTarget={baseCompareTarget}
                          compareTargetOptions={baseCompareOptions}
                          onCompareSourceChange={setBaseCompareSource}
                          onCompareTargetChange={handleBaseCompareTargetChange}
                          leftLabel={baseCompareSource === "published" ? "Published" : "Draft"}
                          rightLabel={baseCompareTargetLabel}
                          fields={[
                            {
                              label: "HTML Layout",
                              leftValue:
                                baseCompareSourceVersion &&
                                baseCompareSourceVersion.htmlLayout,
                              rightValue:
                                baseCompareTargetVersion &&
                                baseCompareTargetVersion.htmlLayout,
                            },
                          ]}
                          metadata={[
                            {
                              label: "Tokens",
                              leftValue:
                                (baseCompareSourceVersion &&
                                  baseCompareSourceVersion.allowedTokens) ||
                                [],
                              rightValue:
                                (baseCompareTargetVersion &&
                                  baseCompareTargetVersion.allowedTokens) ||
                                [],
                            },
                            {
                              label: "Images",
                              leftValue: formatAssetReferenceNames(
                                baseCompareSourceVersion &&
                                  baseCompareSourceVersion.referencedImages
                              ),
                              rightValue: formatAssetReferenceNames(
                                baseCompareTargetVersion &&
                                  baseCompareTargetVersion.referencedImages
                              ),
                            },
                          ]}
                        />
                      ) : null}

                      <NotificationLocalePanel
                        title="Email Content Locales"
                        freshness={contentEditor.translationFreshness}
                        localeStatuses={contentEditor.localeStatuses}
                      />

                      {baseEditor ? (
                        <NotificationLocalePanel
                          title="Email Base Locales"
                          freshness={baseEditor.translationFreshness}
                          localeStatuses={baseEditor.localeStatuses}
                        />
                      ) : null}

                      <NotificationSchedulePanel
                        title="Email Content Schedule"
                        schedule={contentEditor.scheduledPublish}
                        busy={working}
                        onSave={handleSaveContentSchedule}
                        onCancel={handleCancelContentSchedule}
                      />

                      {baseEditor ? (
                        <NotificationSchedulePanel
                          title="Email Base Schedule"
                          schedule={baseEditor.scheduledPublish}
                          busy={working}
                          onSave={handleSaveBaseSchedule}
                          onCancel={handleCancelBaseSchedule}
                        />
                      ) : null}

                      <NotificationDependencyGraphPanel
                        title="Email Content Dependencies"
                        graph={contentDependencyGraph}
                        loading={loadingContentDependencies}
                        state={contentDependencyState}
                        stateOptions={GRAPH_STATE_OPTIONS}
                        onStateChange={(nextState) => {
                          setContentDependencyState(nextState);
                          loadContentDependencies(nextState);
                        }}
                        onRefresh={() => loadContentDependencies(contentDependencyState)}
                        onNodeClick={handleEmailDependencyNodeClick}
                      />

                      {baseEditor ? (
                        <NotificationDependencyGraphPanel
                          title="Email Base Dependencies"
                          graph={baseDependencyGraph}
                          loading={loadingBaseDependencies}
                          state={baseDependencyState}
                          stateOptions={GRAPH_STATE_OPTIONS}
                          onStateChange={(nextState) => {
                            setBaseDependencyState(nextState);
                            loadBaseDependencies(nextState, baseEditor);
                          }}
                          onRefresh={() => loadBaseDependencies(baseDependencyState, baseEditor)}
                          onNodeClick={handleEmailDependencyNodeClick}
                        />
                      ) : null}
                    </Box>

                    <Box mt={4}>
                      <Typography variant="subtitle1" className={classes.sectionTitle}>
                        Content History
                      </Typography>
                      {(contentEditor.history || []).map((revision) => (
                        <div key={revision.notificationTemplateRevisionId} className={classes.historyItem}>
                          <div className={classes.rowInner}>
                            <div>
                              <Typography variant="subtitle2">
                                {revision.action} ({revision.state})
                              </Typography>
                              <Typography variant="body2" color="textSecondary">
                                {revision.provenance} - {formatDate(revision.audit && revision.audit.updatedDateUtc)}
                              </Typography>
                            </div>
                            <div className={classes.toolbarActions}>
                              <Button variant="outlined" size="small" onClick={() => handleLoadContentRevision(revision.notificationTemplateRevisionId)}>
                                View
                              </Button>
                              <Button variant="outlined" size="small" onClick={() => handleRestoreContentRevision(revision.notificationTemplateRevisionId)}>
                                Restore
                              </Button>
                            </div>
                          </div>
                        </div>
                      ))}

                      {selectedContentRevision ? (
                        <Paper className={classes.versionCard}>
                          <Typography variant="subtitle1" className={classes.sectionTitle}>
                            Selected Content Revision
                          </Typography>
                          <Typography variant="subtitle2">Subject</Typography>
                          <Typography variant="body2">
                            {(selectedContentRevision.emailContent && selectedContentRevision.emailContent.subject) || "No subject."}
                          </Typography>
                          <div className={classes.codeBlock}>
                            {(selectedContentRevision.emailContent && selectedContentRevision.emailContent.htmlFragment) || "No HTML."}
                          </div>
                        </Paper>
                      ) : null}
                    </Box>

                    {baseEditor ? (
                      <Box mt={4}>
                        <Typography variant="subtitle1" className={classes.sectionTitle}>
                          Base History
                        </Typography>
                        {(baseEditor.history || []).map((revision) => (
                          <div key={revision.notificationTemplateRevisionId} className={classes.historyItem}>
                            <div className={classes.rowInner}>
                              <div>
                                <Typography variant="subtitle2">
                                  {revision.action} ({revision.state})
                                </Typography>
                                <Typography variant="body2" color="textSecondary">
                                  {revision.provenance} - {formatDate(revision.audit && revision.audit.updatedDateUtc)}
                                </Typography>
                              </div>
                              <div className={classes.toolbarActions}>
                                <Button variant="outlined" size="small" onClick={() => handleLoadBaseRevision(revision.notificationTemplateRevisionId)}>
                                  View
                                </Button>
                                <Button variant="outlined" size="small" onClick={() => handleRestoreBaseRevision(revision.notificationTemplateRevisionId)}>
                                  Restore
                                </Button>
                              </div>
                            </div>
                          </div>
                        ))}

                        {selectedBaseRevision ? (
                          <Paper className={classes.versionCard}>
                            <Typography variant="subtitle1" className={classes.sectionTitle}>
                              Selected Base Revision
                            </Typography>
                            <div className={classes.codeBlock}>
                              {(selectedBaseRevision.emailBase && selectedBaseRevision.emailBase.htmlLayout) || "No HTML."}
                            </div>
                          </Paper>
                        ) : null}
                      </Box>
                    ) : null}
                  </>
                ) : baseEditor ? (
                  <>
                    <div className={classes.editorMeta}>
                      <Chip label={`Base: ${baseEditor.templateKey}`} />
                      <Chip label={`Provenance: ${baseEditor.provenance && baseEditor.provenance.value}`} />
                      {baseEditor.hasDraftChanges ? <Chip color="primary" label="Unpublished draft" /> : null}
                      <Chip color={baseEditor.isLocked ? "secondary" : "default"} label={baseEditor.isLocked ? "Locked" : "Unlocked"} />
                    </div>

                    <Grid container spacing={2}>
                      <Grid item xs={12} md={6}>
                        <Paper className={classes.versionCard}>
                          <Typography variant="subtitle1" className={classes.sectionTitle}>
                            Draft Base Layout
                          </Typography>
                          <TextField
                            id="email-base-mode-html-layout"
                            fullWidth
                            multiline
                            minRows={18}
                            variant="outlined"
                            label="Base HTML Layout"
                            value={(baseEditor.draft && baseEditor.draft.htmlLayout) || ""}
                            onChange={(event) => updateBaseDraft("htmlLayout", event.target.value)}
                            className={classes.field}
                          />
                          <div className={classes.actionRow}>
                            <Button variant="outlined" color="primary" onClick={() => openImageLibrary("base")}>
                              Image Library
                            </Button>
                            <Button color="primary" variant="contained" onClick={handleSaveBase} disabled={saving || working}>
                              {saving ? "Saving..." : "Save Base Draft"}
                            </Button>
                            <Button variant="contained" color="secondary" onClick={handlePublishBase} disabled={saving || working}>
                              Publish Base
                            </Button>
                            <Button variant="outlined" onClick={() => handleToggleBaseLock(baseEditor.isLocked)} disabled={saving || working}>
                              {baseEditor.isLocked ? "Unlock Base" : "Lock Base"}
                            </Button>
                          </div>
                        </Paper>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <Paper className={classes.versionCard}>
                          <Typography variant="subtitle1" className={classes.sectionTitle}>
                            Preview
                          </Typography>
                          <div className={classes.actionRow}>
                            <FormControl variant="outlined" className={classes.selectField}>
                              <InputLabel id="base-preview-state-label">Preview State</InputLabel>
                              <Select
                                labelId="base-preview-state-label"
                                value={previewState}
                                onChange={(event) => setPreviewState(event.target.value)}
                                label="Preview State"
                              >
                                {PREVIEW_STATES.map((option) => (
                                  <MenuItem key={option.value} value={option.value}>
                                    {option.label}
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                            <Button variant="outlined" color="primary" onClick={handlePreview} disabled={working}>
                              Render Preview
                            </Button>
                            <Button
                              variant="contained"
                              color="primary"
                              onClick={handleSendTestEmail}
                              disabled={working}
                              data-testid="email-test-send-button"
                            >
                              Send Test Email
                            </Button>
                          </div>
                          <TextField
                            id="email-base-preview-json"
                            fullWidth
                            multiline
                            minRows={6}
                            variant="outlined"
                            label="Sample token JSON"
                            value={previewJson}
                            onChange={(event) => setPreviewJson(event.target.value)}
                            className={classes.field}
                          />
                          <iframe
                            title="Email base preview"
                            srcDoc={previewHtml || "<p>No preview available.</p>"}
                            className={classes.previewFrame}
                          />
                        </Paper>
                      </Grid>
                    </Grid>

                    <Box mt={4}>
                      <Typography variant="subtitle1" className={classes.sectionTitle}>
                        Lint Issues
                      </Typography>
                      <LintIssuesPanel
                        issues={combinedLintIssues}
                        emptyLabel="No issues detected for this base layout."
                      />
                    </Box>

                    <Box mt={4}>
                      <NotificationChangesPanel
                        title="Email Base Changes"
                        compareSource={baseCompareSource}
                        compareTarget={baseCompareTarget}
                        compareTargetOptions={baseCompareOptions}
                        onCompareSourceChange={setBaseCompareSource}
                        onCompareTargetChange={handleBaseCompareTargetChange}
                        leftLabel={baseCompareSource === "published" ? "Published" : "Draft"}
                        rightLabel={baseCompareTargetLabel}
                        fields={[
                          {
                            label: "HTML Layout",
                            leftValue:
                              baseCompareSourceVersion &&
                              baseCompareSourceVersion.htmlLayout,
                            rightValue:
                              baseCompareTargetVersion &&
                              baseCompareTargetVersion.htmlLayout,
                          },
                        ]}
                        metadata={[
                          {
                            label: "Tokens",
                            leftValue:
                              (baseCompareSourceVersion &&
                                baseCompareSourceVersion.allowedTokens) ||
                              [],
                            rightValue:
                              (baseCompareTargetVersion &&
                                baseCompareTargetVersion.allowedTokens) ||
                              [],
                          },
                          {
                            label: "Images",
                            leftValue: formatAssetReferenceNames(
                              baseCompareSourceVersion &&
                                baseCompareSourceVersion.referencedImages
                            ),
                            rightValue: formatAssetReferenceNames(
                              baseCompareTargetVersion &&
                                baseCompareTargetVersion.referencedImages
                            ),
                          },
                        ]}
                      />

                      <NotificationLocalePanel
                        title="Email Base Locales"
                        freshness={baseEditor.translationFreshness}
                        localeStatuses={baseEditor.localeStatuses}
                      />

                      <NotificationSchedulePanel
                        title="Email Base Schedule"
                        schedule={baseEditor.scheduledPublish}
                        busy={working}
                        onSave={handleSaveBaseSchedule}
                        onCancel={handleCancelBaseSchedule}
                      />

                      <NotificationDependencyGraphPanel
                        title="Email Base Dependencies"
                        graph={baseDependencyGraph}
                        loading={loadingBaseDependencies}
                        state={baseDependencyState}
                        stateOptions={GRAPH_STATE_OPTIONS}
                        onStateChange={(nextState) => {
                          setBaseDependencyState(nextState);
                          loadBaseDependencies(nextState, baseEditor);
                        }}
                        onRefresh={() => loadBaseDependencies(baseDependencyState, baseEditor)}
                        onNodeClick={handleEmailDependencyNodeClick}
                      />
                    </Box>

                    <Box mt={4}>
                      <Typography variant="subtitle1" className={classes.sectionTitle}>
                        Base History
                      </Typography>
                      {(baseEditor.history || []).map((revision) => (
                        <div key={revision.notificationTemplateRevisionId} className={classes.historyItem}>
                          <div className={classes.rowInner}>
                            <div>
                              <Typography variant="subtitle2">
                                {revision.action} ({revision.state})
                              </Typography>
                              <Typography variant="body2" color="textSecondary">
                                {revision.provenance} - {formatDate(revision.audit && revision.audit.updatedDateUtc)}
                              </Typography>
                            </div>
                            <div className={classes.toolbarActions}>
                              <Button variant="outlined" size="small" onClick={() => handleLoadBaseRevision(revision.notificationTemplateRevisionId)}>
                                View
                              </Button>
                              <Button variant="outlined" size="small" onClick={() => handleRestoreBaseRevision(revision.notificationTemplateRevisionId)}>
                                Restore
                              </Button>
                            </div>
                          </div>
                        </div>
                      ))}

                      {selectedBaseRevision ? (
                        <Paper className={classes.versionCard}>
                          <Typography variant="subtitle1" className={classes.sectionTitle}>
                            Selected Base Revision
                          </Typography>
                          <div className={classes.codeBlock}>
                            {(selectedBaseRevision.emailBase && selectedBaseRevision.emailBase.htmlLayout) || "No HTML."}
                          </div>
                        </Paper>
                      ) : null}
                    </Box>
                  </>
                ) : (
                  <div className={classes.emptyState}>
                    <Typography variant="body2">No editor data available.</Typography>
                  </div>
                )}
              </>
            )}
          </Paper>
        </Grid>
      </Grid>

      <TranslationDialog
        open={translationDialogOpen}
        locales={locales}
        selectedLocales={translationLocales}
        onToggleLocale={toggleTranslationLocale}
        onClose={() => {
          setTranslationDialogOpen(false);
          setTranslationLocales([]);
        }}
        onSubmit={handleQueueTranslations}
        busy={working}
      />

      <TestDestinationDialog
        open={testDestinationDialogOpen}
        override={testDestinationOverride}
        busy={working}
        onClose={() => setTestDestinationDialogOpen(false)}
        onSave={handleSaveTestDestination}
        server={testDestinationServer}
      />

      <ImagePickerDialog
        open={imagePickerOpen}
        onClose={() => setImagePickerOpen(false)}
        onInsertReference={handleInsertImageReference}
      />
    </NotificationScaffold>
  );
};

const MigrationReportPage = ({ match, rootMatch }) => {
  const classes = useStyles();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [error, setError] = useState("");

  const loadReport = async () => {
    setLoading(true);
    setError("");

    try {
      const response = await api.get(
        "/api/v2/ServiceCenter/notification-templates/migration-report"
      );
      setReport(response.data || null);
    } catch (loadError) {
      setError("Unable to load the migration report.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadReport();
  }, []);

  const filteredItems = useMemo(
    () => filterMigrationItems(report && report.items, search, statusFilter),
    [report, search, statusFilter]
  );

  return (
    <NotificationScaffold
      match={match}
      rootMatch={rootMatch}
      heading="Migration Report"
      subtitle="Track which templates are fully managed in Cosmos, which ones still depend on legacy file fallbacks, and which file sources are still in play."
    >
      <StatusBanners error={error} />

      <div className={classes.selectRow}>
        <TextField
          id="migration-search"
          fullWidth
          variant="outlined"
          label="Search migration report"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
        />
        <Button variant="outlined" onClick={loadReport} disabled={loading}>
          Refresh
        </Button>
      </div>

      <div className={classes.filterRow}>
        {MIGRATION_STATUSES.map((option) => (
          <Chip
            key={option.value}
            clickable
            color={statusFilter === option.value ? "primary" : "default"}
            label={option.label}
            onClick={() => setStatusFilter(option.value)}
          />
        ))}
      </div>

      {report ? (
        <div className={classes.editorMeta}>
          <Chip label={`Managed: ${report.managedCount || 0}`} />
          <Chip label={`Partial: ${report.partialCount || 0}`} />
          <Chip label={`Legacy dependent: ${report.legacyDependentCount || 0}`} />
        </div>
      ) : null}

      {loading ? (
        <div className={classes.loadingWrap}>
          <CircularProgress />
        </div>
      ) : filteredItems.length === 0 ? (
        <div className={classes.emptyState}>
          <Typography variant="subtitle1">No migration rows found</Typography>
          <Typography variant="body2">
            Adjust the filters or refresh the report.
          </Typography>
        </div>
      ) : (
        <Grid container spacing={2}>
          {filteredItems.map((item) => (
            <Grid item xs={12} md={6} key={item.templateKey} data-testid={`migration-row-${item.templateKey}`}>
              <Paper className={classes.versionCard}>
                <Typography variant="subtitle1" className={classes.rowTitle}>
                  {item.displayName}
                </Typography>
                <Typography className={classes.rowKey}>{item.templateKey}</Typography>
                <div className={classes.chipRow}>
                  <Chip size="small" color={item.status === "managed" ? "primary" : item.status === "partial" ? "secondary" : "default"} label={item.status} />
                  {item.legacyTemplateExists ? <Chip size="small" label="Legacy file present" /> : null}
                </div>
                <Box mt={2}>
                  <Typography variant="subtitle2">Source Template File</Typography>
                  <Typography variant="body2">
                    {item.sourceTemplateFile || "No legacy source file tracked."}
                  </Typography>
                </Box>
                <Box mt={2}>
                  <Typography variant="subtitle2">Brands</Typography>
                  <Typography variant="body2">{(item.brands || []).join(", ") || "None"}</Typography>
                </Box>
                <Box mt={2}>
                  <Typography variant="subtitle2">Managed Kinds</Typography>
                  <Typography variant="body2">{(item.managedKinds || []).join(", ") || "None"}</Typography>
                </Box>
                <Box mt={2}>
                  <Typography variant="subtitle2">Missing Managed Kinds</Typography>
                  <Typography variant="body2">
                    {(item.missingManagedKinds || []).join(", ") || "None"}
                  </Typography>
                </Box>
              </Paper>
            </Grid>
          ))}
        </Grid>
      )}
    </NotificationScaffold>
  );
};

const ImageLibraryPage = ({ match, rootMatch }) => {
  const classes = useStyles();
  const history = useHistory();
  const location = useLocation();
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [working, setWorking] = useState(false);
  const [search, setSearch] = useState("");
  const [brand, setBrand] = useState("");
  const [tag, setTag] = useState("");
  const [includeArchived, setIncludeArchived] = useState(false);
  const [imageFilter, setImageFilter] = useState("");
  const [uploadFile, setUploadFile] = useState(null);
  const [displayName, setDisplayName] = useState("");
  const [altText, setAltText] = useState("");
  const [tagText, setTagText] = useState("");
  const [brandText, setBrandText] = useState("");
  const [status, setStatus] = useState("");
  const [error, setError] = useState("");
  const [usageDialogOpen, setUsageDialogOpen] = useState(false);
  const [selectedUsage, setSelectedUsage] = useState(null);
  const [replaceDialogOpen, setReplaceDialogOpen] = useState(false);
  const [replaceAsset, setReplaceAsset] = useState(null);
  const [replaceFile, setReplaceFile] = useState(null);
  const [replaceDisplayName, setReplaceDisplayName] = useState("");
  const [replaceAltText, setReplaceAltText] = useState("");
  const [replaceTagText, setReplaceTagText] = useState("");
  const [replaceBrandText, setReplaceBrandText] = useState("");
  const [dependencyState, setDependencyState] = useState("All");
  const [dependencyGraph, setDependencyGraph] = useState(null);
  const [dependencyAsset, setDependencyAsset] = useState(null);
  const [loadingDependencies, setLoadingDependencies] = useState(false);

  const loadAssets = async () => {
    setLoading(true);
    setError("");

    try {
      const response = await api.get("/api/v2/ServiceCenter/notification-templates/images", {
        params: {
          search,
          brand: brand || undefined,
          tag: tag || undefined,
          includeArchived: includeArchived || imageFilter === "archived",
          filter: imageFilter || undefined,
        },
      });

      setAssets((response.data && response.data.items) || []);
    } catch (loadError) {
      setError(getApiErrorMessage(loadError, "Unable to load notification image assets."));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAssets();
  }, []);

  useEffect(() => {
    loadAssets();
  }, [imageFilter]);

  useEffect(() => {
    const jump = location.state && location.state.notificationJump;
    if (!jump || jump.type !== "image" || !jump.assetId) {
      return;
    }

    setDependencyAsset({
      notificationImageAssetId: jump.assetId,
      displayName: "Selected Image Asset",
    });
    loadImageDependencies(jump.assetId, dependencyState);
    history.replace(location.pathname);
  }, [dependencyState, history, location.pathname, location.state]);

  const handleUpload = async () => {
    if (!uploadFile) {
      setError("Choose an image before uploading.");
      return;
    }

    if (!altText.trim()) {
      setError("Alt text is required.");
      return;
    }

    setWorking(true);
    setStatus("");
    setError("");

    try {
      const formData = new FormData();
      formData.append("file", uploadFile);

      if (displayName) {
        formData.append("displayName", displayName);
      }

      if (altText) {
        formData.append("altText", altText);
      }

      parseCsv(tagText).forEach((value) => formData.append("tags", value));

      parseCsv(brandText).forEach((value) => formData.append("brands", value));

      await api.post("/api/v2/ServiceCenter/notification-templates/images", formData);
      setStatus("Image uploaded to the shared template library.");
      setUploadFile(null);
      setDisplayName("");
      setAltText("");
      setTagText("");
      setBrandText("");
      await loadAssets();
    } catch (uploadError) {
      setError(getApiErrorMessage(uploadError, "Unable to upload that image."));
    } finally {
      setWorking(false);
    }
  };

  const handleCopy = async (value, label) => {
    try {
      await copyToClipboard(value);
      setStatus(`${label} copied.`);
    } catch (copyError) {
      setError(`Unable to copy the ${label.toLowerCase()}.`);
    }
  };

  const handleLoadUsage = async (assetId) => {
    setWorking(true);
    setError("");

    try {
      const response = await api.get(
        `/api/v2/ServiceCenter/notification-templates/images/${assetId}/usage`
      );
      setSelectedUsage(response.data || null);
      setUsageDialogOpen(true);
    } catch (loadError) {
      setError("Unable to load image usage.");
    } finally {
      setWorking(false);
    }
  };

  const handleToggleArchive = async (asset) => {
    setWorking(true);
    setStatus("");
    setError("");

    try {
      await api.post(
        `/api/v2/ServiceCenter/notification-templates/images/${asset.notificationImageAssetId}/${asset.isArchived ? "restore" : "archive"}`
      );

      setStatus(asset.isArchived ? "Image restored." : "Image archived.");
      await loadAssets();
    } catch (archiveError) {
      setError(getApiErrorMessage(archiveError, "Unable to update that image asset."));
    } finally {
      setWorking(false);
    }
  };

  const openReplaceDialog = (asset) => {
    setReplaceAsset(asset);
    setReplaceFile(null);
    setReplaceDisplayName(asset.displayName || "");
    setReplaceAltText(asset.altText || "");
    setReplaceTagText((asset.tags || []).join(", "));
    setReplaceBrandText((asset.brands || []).join(", "));
    setReplaceDialogOpen(true);
  };

  const handleReplaceAsset = async () => {
    if (!replaceAsset || !replaceFile) {
      setError("Choose a replacement image first.");
      return;
    }

    if (!replaceAltText.trim()) {
      setError("Alt text is required.");
      return;
    }

    setWorking(true);
    setStatus("");
    setError("");

    try {
      const formData = new FormData();
      formData.append("file", replaceFile);
      formData.append("displayName", replaceDisplayName);
      formData.append("altText", replaceAltText);
      parseCsv(replaceTagText).forEach((value) => formData.append("tags", value));
      parseCsv(replaceBrandText).forEach((value) => formData.append("brands", value));

      await api.put(
        `/api/v2/ServiceCenter/notification-templates/images/${replaceAsset.notificationImageAssetId}`,
        formData
      );

      setReplaceDialogOpen(false);
      setStatus("Image replaced in place.");
      await loadAssets();
      if (
        dependencyAsset &&
        dependencyAsset.notificationImageAssetId === replaceAsset.notificationImageAssetId
      ) {
        await loadImageDependencies(replaceAsset.notificationImageAssetId, dependencyState);
      }
    } catch (replaceError) {
      setError(getApiErrorMessage(replaceError, "Unable to replace that image."));
    } finally {
      setWorking(false);
    }
  };

  const loadImageDependencies = async (assetId, nextState = dependencyState) => {
    setLoadingDependencies(true);
    setError("");

    try {
      const response = await api.get(
        `/api/v2/ServiceCenter/notification-templates/images/${assetId}/dependencies`,
        {
          params: {
            state: nextState === "All" ? undefined : nextState,
          },
        }
      );

      setDependencyGraph(response.data || null);
      const matchingAsset = assets.find(
        (item) => item.notificationImageAssetId === assetId
      );
      setDependencyAsset(
        matchingAsset || {
          notificationImageAssetId: assetId,
          displayName: "Selected Image Asset",
        }
      );
    } catch (dependencyError) {
      setError(getApiErrorMessage(dependencyError, "Unable to load image dependencies."));
    } finally {
      setLoadingDependencies(false);
    }
  };

  const handleDependencyNodeClick = (node) => {
    if (!node || !node.templateKind || !node.templateKey) {
      return;
    }

    if (node.templateKind === "PushTemplate") {
      history.push(`${rootMatch.url}/push`, {
        notificationJump: {
          templateKind: node.templateKind,
          templateKey: node.templateKey,
          brand: node.brand,
          locale: node.locale,
        },
      });
      return;
    }

    history.push(`${rootMatch.url}/email`, {
      notificationJump: {
        templateKind: node.templateKind,
        templateKey: node.templateKey,
        brand: node.brand,
        locale: node.locale,
      },
    });
  };

  return (
    <NotificationScaffold
      match={match}
      rootMatch={rootMatch}
      heading="Image Library"
      subtitle="Upload and manage shared email assets, copy reusable references, and inspect where each image is currently used before archiving it."
    >
      <StatusBanners status={status} error={error} />

      <Grid container spacing={3}>
        <Grid item xs={12} md={4}>
          <Paper className={classes.listPanel}>
            <Typography variant="subtitle1" className={classes.sectionTitle}>
              Upload Asset
            </Typography>
            <input
              type="file"
              accept="image/*"
              className={classes.plainInput}
              data-testid="image-upload-input"
              onChange={(event) => setUploadFile(event.target.files && event.target.files[0])}
            />
            <TextField
              id="image-display-name"
              fullWidth
              variant="outlined"
              label="Display Name"
              value={displayName}
              onChange={(event) => setDisplayName(event.target.value)}
              className={classes.field}
            />
            <TextField
              id="image-alt-text"
              fullWidth
              variant="outlined"
              label="Alt Text"
              value={altText}
              onChange={(event) => setAltText(event.target.value)}
              className={classes.field}
            />
            <TextField
              id="image-tag-text"
              fullWidth
              variant="outlined"
              label="Tags (comma separated)"
              value={tagText}
              onChange={(event) => setTagText(event.target.value)}
              className={classes.field}
            />
            <TextField
              id="image-brand-text"
              fullWidth
              variant="outlined"
              label="Brands (comma separated)"
              value={brandText}
              onChange={(event) => setBrandText(event.target.value)}
              className={classes.field}
            />
            <Button
              fullWidth
              color="primary"
              variant="contained"
              onClick={handleUpload}
              disabled={working}
              data-testid="image-upload-button"
            >
              {working ? "Uploading..." : "Upload Image"}
            </Button>
          </Paper>
        </Grid>

        <Grid item xs={12} md={8}>
          <Paper className={classes.editorPanel}>
            <div className={classes.selectRow}>
              <TextField
                fullWidth
                variant="outlined"
                label="Search images"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
              />
              <TextField
                variant="outlined"
                label="Brand"
                value={brand}
                onChange={(event) => setBrand(event.target.value)}
              />
              <TextField
                variant="outlined"
                label="Tag"
                value={tag}
                onChange={(event) => setTag(event.target.value)}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    color="primary"
                    checked={includeArchived}
                    onChange={(event) => setIncludeArchived(event.target.checked)}
                  />
                }
                label="Include archived"
              />
              <Button variant="outlined" onClick={loadAssets} disabled={loading}>
                Refresh
              </Button>
            </div>

            <div className={classes.filterRow}>
              {IMAGE_LIBRARY_FILTERS.map((option) => (
                <Chip
                  key={option.value || "all"}
                  clickable
                  color={imageFilter === option.value ? "primary" : "default"}
                  label={option.label}
                  onClick={() => setImageFilter(option.value)}
                  data-testid={`image-filter-${(option.value || "all").replace(/\s+/g, "-")}`}
                />
              ))}
            </div>

            {loading ? (
              <div className={classes.loadingWrap}>
                <CircularProgress />
              </div>
            ) : assets.length === 0 ? (
              <div className={classes.emptyState}>
                <Typography variant="subtitle1">No image assets found</Typography>
                <Typography variant="body2">
                  Adjust the filters or upload a new shared asset.
                </Typography>
              </div>
            ) : (
              <Grid container spacing={2}>
                {assets.map((asset) => (
                  <Grid
                    item
                    xs={12}
                    md={6}
                    key={asset.notificationImageAssetId}
                    data-testid={`image-asset-card-${asset.notificationImageAssetId}`}
                  >
                    <Paper className={classes.imageCard}>
                      <img
                        src={asset.url}
                        alt={asset.altText || asset.displayName}
                        className={classes.imagePreview}
                      />
                      <Typography variant="subtitle1" className={classes.rowTitle}>
                        {asset.displayName}
                      </Typography>
                      <Typography className={classes.rowKey}>{asset.fileName}</Typography>
                      <Typography variant="body2" color="textSecondary">
                        {asset.altText || "No alt text"} - {formatBytes(asset.sizeBytes)}
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        {(asset.widthPx && asset.heightPx
                          ? `${asset.widthPx}x${asset.heightPx}px`
                          : "Dimensions unavailable")} - v{asset.version || 1}
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        Hash: {asset.sha256 || "Not calculated"}
                      </Typography>
                      <div className={classes.chipRow}>
                        {(asset.tags || []).map((assetTag) => (
                          <Chip key={`${asset.notificationImageAssetId}-${assetTag}`} size="small" label={assetTag} />
                        ))}
                        {(asset.brands || []).map((assetBrand) => (
                          <Chip key={`${asset.notificationImageAssetId}-${assetBrand}`} size="small" label={assetBrand} />
                        ))}
                        {asset.isArchived ? <Chip size="small" color="secondary" label="Archived" /> : null}
                        {asset.isOrphaned ? <Chip size="small" label="Unused" /> : null}
                        {asset.hasDuplicateHash ? <Chip size="small" color="secondary" label="Duplicate hash" /> : null}
                        {asset.draftUsageCount ? <Chip size="small" label={`Draft refs: ${asset.draftUsageCount}`} /> : null}
                        {asset.publishedUsageCount ? <Chip size="small" color="primary" label={`Published refs: ${asset.publishedUsageCount}`} /> : null}
                      </div>
                      <div className={classes.actionRow}>
                        <Button
                          variant="outlined"
                          onClick={() => handleCopy(asset.url, "URL")}
                          data-testid={`image-copy-url-${asset.notificationImageAssetId}`}
                        >
                          Copy URL
                        </Button>
                        <Button
                          variant="outlined"
                          onClick={() => handleCopy(asset.htmlSnippet, "HTML")}
                          data-testid={`image-copy-html-${asset.notificationImageAssetId}`}
                        >
                          Copy HTML
                        </Button>
                        <Button
                          variant="outlined"
                          onClick={() => handleLoadUsage(asset.notificationImageAssetId)}
                          data-testid={`image-usage-${asset.notificationImageAssetId}`}
                        >
                          Usage
                        </Button>
                        <Button
                          variant="outlined"
                          onClick={() => loadImageDependencies(asset.notificationImageAssetId, dependencyState)}
                          data-testid={`image-dependencies-${asset.notificationImageAssetId}`}
                        >
                          Dependencies
                        </Button>
                        <Button
                          variant="outlined"
                          color="primary"
                          onClick={() => openReplaceDialog(asset)}
                          data-testid={`image-replace-${asset.notificationImageAssetId}`}
                        >
                          Replace
                        </Button>
                        <Button
                          variant="outlined"
                          onClick={() => handleToggleArchive(asset)}
                          disabled={working}
                          data-testid={`image-archive-${asset.notificationImageAssetId}`}
                        >
                          {asset.isArchived ? "Restore" : "Archive"}
                        </Button>
                      </div>
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            )}

            <NotificationDependencyGraphPanel
              title={
                dependencyAsset
                  ? `${dependencyAsset.displayName || "Image"} Dependencies`
                  : "Image Dependencies"
              }
              graph={dependencyGraph}
              loading={loadingDependencies}
              state={dependencyState}
              stateOptions={GRAPH_STATE_OPTIONS_WITH_ALL}
              onStateChange={(nextState) => {
                setDependencyState(nextState);
                if (dependencyAsset) {
                  loadImageDependencies(
                    dependencyAsset.notificationImageAssetId,
                    nextState
                  );
                }
              }}
              onRefresh={() =>
                dependencyAsset
                  ? loadImageDependencies(
                      dependencyAsset.notificationImageAssetId,
                      dependencyState
                    )
                  : null
              }
              onNodeClick={handleDependencyNodeClick}
            />
          </Paper>
        </Grid>
      </Grid>

      <ImageUsageDialog
        open={usageDialogOpen}
        onClose={() => setUsageDialogOpen(false)}
        usage={selectedUsage}
      />

      <Dialog
        open={replaceDialogOpen}
        onClose={() => setReplaceDialogOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Replace Image In Place</DialogTitle>
        <DialogContent dividers>
          <input
            type="file"
            accept="image/png,image/jpeg,image/gif"
            className={classes.plainInput}
            data-testid="image-replace-upload-input"
            onChange={(event) =>
              setReplaceFile(event.target.files && event.target.files[0])
            }
          />
          <TextField
            fullWidth
            variant="outlined"
            label="Display Name"
            value={replaceDisplayName}
            onChange={(event) => setReplaceDisplayName(event.target.value)}
            className={classes.field}
          />
          <TextField
            fullWidth
            variant="outlined"
            label="Alt Text"
            value={replaceAltText}
            onChange={(event) => setReplaceAltText(event.target.value)}
            className={classes.field}
          />
          <TextField
            fullWidth
            variant="outlined"
            label="Tags (comma separated)"
            value={replaceTagText}
            onChange={(event) => setReplaceTagText(event.target.value)}
            className={classes.field}
          />
          <TextField
            fullWidth
            variant="outlined"
            label="Brands (comma separated)"
            value={replaceBrandText}
            onChange={(event) => setReplaceBrandText(event.target.value)}
            className={classes.field}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReplaceDialogOpen(false)}>Cancel</Button>
          <Button
            color="primary"
            variant="contained"
            onClick={handleReplaceAsset}
            disabled={working}
          >
            {working ? "Replacing..." : "Replace Image"}
          </Button>
        </DialogActions>
      </Dialog>
    </NotificationScaffold>
  );
};

const NotificationRoutes = ({ match }) => (
  <Switch>
    <Redirect exact from={match.url} to={`${match.url}/push`} />
    <Route
      path={`${match.url}/push`}
      render={(props) => <PushTemplatesPage {...props} rootMatch={match} />}
    />
    <Route
      path={`${match.url}/email`}
      render={(props) => <EmailTemplatesPage {...props} rootMatch={match} />}
    />
    <Route
      path={`${match.url}/migration`}
      render={(props) => <MigrationReportPage {...props} rootMatch={match} />}
    />
    <Route
      path={`${match.url}/images`}
      render={(props) => <ImageLibraryPage {...props} rootMatch={match} />}
    />
    <Redirect to={`${match.url}/push`} />
  </Switch>
);

export default NotificationRoutes;
